/**
 * Block 2 Practice scenarios catalog · B2-API-NN / B2-SQL-NN / B2-WEB-NN.
 * Source: COORDINATION/app_rework_2026-04-29/13-sandbox-roadmap-s07-s18.md (S07-S12) +
 *         per-Sprint topic alignment (feedback_qa_studio_practice_tasks_topic_alignment).
 *
 * Numbering scheme (per Stage A D1 · B+C combo):
 *   B2-API-NN — S09 (HTTP/REST API basics) + S10 (Postman MASTER)
 *   B2-SQL-NN — S08 (SQL basic) + S11 (SQL deep + DB)
 *   B2-WEB-NN — S07 (Workspace tools) + S12 (Performance + Security)
 *
 * Voice continuity: ADHD-novice register · single-author voice anchor от Block 1 PR-01..PR-33.
 * Tech complexity ramp natural for конец 2-го месяца обучения (HTTP methods, status codes,
 * SQL JOINs, browser DevTools допустимы; senior frameworks like SFDIPOT — НЕ без teach-block).
 *
 * mountStrategy:
 *   'iframe-route'    — Atelier sub-app real route, MSW serves /api/*
 *   'iframe-srcdoc'   — embedded HTML doc with custom interactive surface
 *   'inline-stub'     — placeholder · pending Phase 2 iframe Atelier shell
 *
 * W2.1 authoring batch order:
 *   1. B2-API-01..03  · S09 first MSW scenarios (proof-of-pattern using new api-contract-violation kind)
 *   2. B2-API-04..09  · rest of S09 + S10 Postman authoring
 *   3. B2-SQL-01..06  · S08 sqlite-wasm playground (customModule pattern)
 *   4. B2-SQL-07..12  · S11 SQL deep
 *   5. B2-WEB-01..06  · S07 Workspace tools (Author mode dominant · Jira/TestRail/Confluence)
 *   6. B2-WEB-07..15  · S12 Perf + Security (mixed kinds · auth-flaw + customModule)
 */

import type {
  ApiContractViolationBug,
  AuthFlawBug,
  Scenario,
} from '@/types';

// ────────────────────────────────────────────────────────────────────────────
// Sprint 09 · HTTP & REST API basics — B2-API-01..09
// ────────────────────────────────────────────────────────────────────────────

const B2_API_01_BUG: ApiContractViolationBug = {
  id: 'B1',
  kind: 'api-contract-violation',
  endpoint: 'GET /api/artworks/recent',
  hotspot: '[data-api-response-field="artistAvatar"]',
  expectedShape:
    'Array<{ id, title, artistId, artistAvatar, thumbnailUrl, priceRub, createdAt }>',
  actualResponse:
    'Array<{ id, title, artistId, thumbnailUrl, priceRub, createdAt }> — поле artistAvatar отсутствует во всех объектах ответа',
  violationType: 'missing-field',
  violationField: 'artistAvatar',
  authorNote:
    'MSW handler для /api/artworks/recent не включает artistAvatar в ответ · frontend читает undefined → fallback к старой кэш-аватарке. PRD §API.4 контракта: artistAvatar обязательное поле в каждом объекте artwork. Hotspot = JSON viewer cell, где должно было быть поле artistAvatar (показывается как «⚠ missing» в API Explorer overlay).',
};

const B2_API_01: Scenario = {
  id: 'B2-API-01',
  sprint: 9,
  tier: 'required',
  mode: 'investigate',
  rank: 'api-master-junior',
  startRoute: '/feed/recent',
  mountStrategy: 'iframe-route',
  title: 'API контракт · пропавшее поле в свежей ленте',
  brief:
    'Лера сменила аватарку, но в карусели «Свежие работы» висит старая. Серж: «Глянь, что возвращает /api/artworks/recent. Одно из полей не такое, как в спеке». Найди поле в API Explorer, нарушающее контракт.',
  taskMd: [
    '**Контекст.** Артистка Лера вчера ночью обновила аватарку. Утром пишет в поддержку: «На моём профиле новая аватарка, а в карусели «Свежие работы» — старая. Не баг ли это?»',
    '',
    '**Что делать.**',
    '1. Открой Atelier → жми «Свежие работы» вверху ленты. Карусель загрузится.',
    '2. Внизу справа есть кнопка **🌐 API** — открой API Explorer overlay. Atelier уже вызвал `/api/artworks/recent` при загрузке ленты, ответ виден в response viewer.',
    '3. В боковой панели Explorer-а открыт **PRD §API.4 · Контракт `/api/artworks/recent`**. Там список полей, которые должны прийти в каждом объекте artwork.',
    '4. Сравни response с контрактом. Найди поле, нарушающее контракт.',
    '5. Кликни на это поле в JSON viewer — это твой ответ.',
    '',
    '**Что важно.** Здесь не баг в коде — здесь рассогласование между API и его контрактом. Это категория ошибок, которая в Block 1 не встречалась: API возвращает «технически валидный» JSON, но не тот, который заявлен. Frontend ожидает одно, получает другое — отсюда баг в UI.',
  ].join('\n'),
  bugs: [B2_API_01_BUG],
  hints: [
    {
      text: 'Перечитай вопрос Леры. Она говорит про аватарку. Проверь — есть ли в response поле, связанное с аватаркой художника?',
    },
    {
      text: 'Открой PRD §API.4 в боковой панели. Сравни список полей контракта с полями response. Каких полей контракта не хватает в response?',
    },
    {
      text: 'Поле `artistAvatar` отсутствует во всех объектах response. Frontend читает `artwork.artistAvatar` → получает undefined → подставляет старую кэшированную аватарку. Кликни на JSON-cell, где должно быть это поле (там подсветка «⚠ missing»).',
    },
  ],
  scoring: { baseScore: 6, firstAttemptBonus: 1, hintPenalty: 1, maxAttempts: 3 },
  achievementTriggers: [
    'detective-junior',
    'sprint-09-required',
    'api-master',
  ],
  estimateMin: 15,
  skills: { API: 3, 'Practice Lab': 5, 'Requirements Analysis': 1 },
  requires: ['msw-api'],
};

// ─── B2-API-02 · S09 · wrong-type (price as string breaks numeric sort) ──────

const B2_API_02_BUG: ApiContractViolationBug = {
  id: 'B1',
  kind: 'api-contract-violation',
  endpoint: 'GET /api/artworks/listings',
  hotspot: '[data-api-response-field="priceRub"]',
  expectedShape:
    'priceRub: number (целое число рублей · напр. 1500)',
  actualResponse:
    'priceRub: string (строка в кавычках · напр. "1500") — все объекты response',
  violationType: 'wrong-type',
  violationField: 'priceRub',
  authorNote:
    'MSW handler сериализует priceRub как string ("1500") вместо number (1500). PRD §API.4 контракт priceRub = number. Frontend читает поле, при сортировке lexicographic compare даёт порядок "1099" < "200" вместо численного 200 < 1099. Hotspot = JSON viewer cell для priceRub (помечен type indicator "string" вместо "number").',
};

const B2_API_02: Scenario = {
  id: 'B2-API-02',
  sprint: 9,
  tier: 'required',
  mode: 'investigate',
  rank: 'api-master-junior',
  startRoute: '/listings',
  mountStrategy: 'iframe-route',
  title: 'API контракт · цена приходит строкой',
  brief:
    'Покупатель Аня жалуется: сортировка по цене даёт странный порядок — 100, 1099, 1500, 200, 300. Серж: «Глянь типы полей в /api/artworks/listings, может там не number а что-то другое».',
  taskMd: [
    '**Контекст.** Покупательница Аня заходит в листинг работ, нажимает «по цене ↑», видит порядок: `100 ₽ → 1099 ₽ → 1500 ₽ → 200 ₽ → 300 ₽ → 4500 ₽`. Это не возрастающий порядок. Аня пишет в поддержку: «Сортировка сломана».',
    '',
    '**Что делать.**',
    '1. Открой `/listings`, нажми кнопку сортировки **«по цене ↑»** — увидишь странный порядок выше.',
    '2. Открой **🌐 API** Explorer (внизу справа). Endpoint `/api/artworks/listings` уже вызван при загрузке.',
    '3. В response viewer типы полей подсвечены: `"123"` (строка с кавычками) vs `123` (число без кавычек). Глянь объекты в массиве response.',
    '4. Сравни типы полей в response с типами в **PRD §API.4**.',
    '5. Кликни на поле, нарушающее контракт.',
    '',
    '**Что важно.** API формально вернул 200 OK с валидным JSON — нет «сломанного» ответа. Но тип одного из полей не соответствует контракту. Frontend технически работает (нет crash), но логика на этом поле начинает вести себя неожиданно. Это типовой API-баг: вместо явной ошибки получаем «тихое» нарушение типа.',
  ].join('\n'),
  bugs: [B2_API_02_BUG],
  hints: [
    {
      text: 'Если сравнить как строки `"1099"` и `"200"` — `"1099"` < `"200"` (потому что символ `1` < `2`). Это даёт подсказку про тип данных. Какое поле сортируется?',
    },
    {
      text: 'Открой API Explorer. Какой тип у поля `priceRub` в response? Какой требует PRD §API.4?',
    },
    {
      text: 'PRD говорит `priceRub: number`. API возвращает `priceRub` как строку `"1500"` (с кавычками). Frontend сортирует по полю как строкам → лексикографический порядок. Кликни на поле `priceRub` в любом объекте response viewer-а.',
    },
  ],
  scoring: { baseScore: 6, firstAttemptBonus: 1, hintPenalty: 1, maxAttempts: 3 },
  achievementTriggers: [
    'detective-junior',
    'sprint-09-required',
    'api-master',
  ],
  estimateMin: 15,
  skills: { API: 3, 'Practice Lab': 5, 'Requirements Analysis': 1 },
  requires: ['msw-api'],
};

// ─── B2-API-03 · S09 · wrong-status-code (sold-out returns 200) ──────────────

const B2_API_03_BUG: ApiContractViolationBug = {
  id: 'B1',
  kind: 'api-contract-violation',
  endpoint: 'POST /api/cart/add',
  hotspot: '[data-api-response-field="status"]',
  expectedShape:
    'Статус 409 Conflict + body { error: "out_of_stock" } при попытке добавить распроданную работу',
  actualResponse:
    'Статус 200 OK + body { success: true } — даже когда работа помечена как проданная (`status="sold"`)',
  violationType: 'wrong-status-code',
  authorNote:
    'MSW handler для POST /api/cart/add не проверяет artwork.status === "sold" перед возвратом success. Возвращает 200 OK для всех cases. PRD §API.5 контракт: 200 OK при успехе, 400 при invalid input, 409 Conflict при out-of-stock. Frontend видит 200 → показывает «Добавлено!» → но при checkout корзина пустая (server-side validation отбраковывает проданную работу). Hotspot = status code cell в response viewer (показано «200 OK», должно быть «409 Conflict»).',
};

const B2_API_03: Scenario = {
  id: 'B2-API-03',
  sprint: 9,
  tier: 'required',
  mode: 'investigate',
  rank: 'api-master-junior',
  startRoute: '/listings',
  mountStrategy: 'iframe-route',
  title: 'API контракт · «успех» при добавлении проданной работы',
  brief:
    'Покупательница Аня добавила распроданную работу в корзину — UI показал «Добавлено!». Но в `/cart` пусто. Серж: «Что возвращает POST /api/cart/add для проданной работы? Должен 409 Conflict».',
  taskMd: [
    '**Контекст.** Лера в поддержку: «Моя работа `Vapor #07` уже продана 3 дня. Но листинг показывает её как доступную, и покупательница Аня жалуется что купила её, а заказа нет в системе». Серж говорит: «Проверь как ведёт себя API при попытке добавить распроданную работу».',
    '',
    '**Что делать.**',
    '1. Открой `/listings`. Найди работу **«Vapor #07»** (на карточке есть бейдж 🔒 продана).',
    '2. Нажми **«В корзину»**. UI покажет toast «Добавлено!» (зелёный).',
    '3. Перейди в `/cart` — корзина пустая. Это и есть симптом.',
    '4. Открой **🌐 API** Explorer. Найди последний POST `/api/cart/add` (Atelier его вызвал на шаге 2).',
    '5. Глянь статус-код response. Сравни с **PRD §API.5** контракт: какой статус-код должен быть для распроданной работы?',
    '6. Кликни на статус-код в response viewer.',
    '',
    '**Что важно.** Здесь два рассогласования: (а) API вернул не тот статус-код; (б) frontend поверил статусу 200 и показал success. Баг — в API. Frontend бы повёл себя правильно, если бы API соблюдал контракт. Это пример как нарушение API-контракта приводит к UX-проблеме у конечного пользователя.',
  ].join('\n'),
  bugs: [B2_API_03_BUG],
  hints: [
    {
      text: 'API сказал «успех» (200), а корзина пустая. Значит сервер где-то отбраковал запрос между `/cart/add` и `/cart`. Если бы API сразу честно сказал «нельзя добавить проданную работу» — frontend показал бы ошибку правильно.',
    },
    {
      text: 'Открой PRD §API.5 для POST `/api/cart/add`: какие статус-коды должен возвращать API? 200 OK при успехе. 400 при invalid input. 409 Conflict при out-of-stock. Что вернул API в твоём кейсе?',
    },
    {
      text: 'API вернул `200 OK` даже для распроданной работы. По контракту должно было быть `409 Conflict`. Кликни на статус-код в response viewer (показано «200 OK», должно было «409 Conflict»).',
    },
  ],
  scoring: { baseScore: 6, firstAttemptBonus: 1, hintPenalty: 1, maxAttempts: 3 },
  achievementTriggers: [
    'detective-junior',
    'sprint-09-required',
    'api-master',
  ],
  estimateMin: 15,
  skills: { API: 3, 'Practice Lab': 5, 'Requirements Analysis': 1 },
  requires: ['msw-api'],
};

// ─── B2-API-04 · S09 · auth-flaw (public endpoint leaks admin-only field) ───

const B2_API_04_BUG: AuthFlawBug = {
  id: 'B1',
  kind: 'auth-flaw',
  hotspot: '[data-api-response-field="moderationFlags"]',
  flawType: 'public-endpoint-leak',
  expectedBehavior:
    'GET /api/artworks/recent для unauthenticated request возвращает только публичные поля: id, title, artistId, artistAvatar, thumbnailUrl, priceRub, createdAt',
  actualBehavior:
    'Endpoint возвращает дополнительное поле moderationFlags (массив с внутренними moderation tags · напр. ["pending-review", "user-reported-spam"]) — это admin-only данные, не должны утекать в публичный response',
  authorNote:
    'MSW handler не фильтрует поля по auth-уровню запроса. Публичный endpoint /api/artworks/recent (open для unauthenticated) возвращает поле moderationFlags, которое должно быть видимо только moderator role. Это утечка внутренних данных. PRD §API.4 + Security Spec §2.3 контракт: moderationFlags не в публичной surface. Hotspot = JSON viewer cell для moderationFlags в любом объекте.',
};

const B2_API_04: Scenario = {
  id: 'B2-API-04',
  sprint: 9,
  tier: 'required',
  mode: 'investigate',
  rank: 'api-master-junior',
  startRoute: '/feed/recent',
  mountStrategy: 'iframe-route',
  title: 'API безопасность · утечка admin-only поля',
  brief:
    'Серж заметил подозрительные данные в публичной ленте. «Глянь, что возвращает /api/artworks/recent. По спеке там должны быть только публичные поля, но что-то ещё мелькает».',
  taskMd: [
    '**Контекст.** Серж проводит security audit перед релизом v1.0. Открыл DevTools, посмотрел network в публичной ленте, увидел что-то странное в response — поле, которого там быть не должно.',
    '',
    '**Что делать.**',
    '1. Открой Atelier в режиме «гость» (без логина). Жми «Свежие работы».',
    '2. Открой **🌐 API** Explorer. Endpoint `/api/artworks/recent` уже вызван.',
    '3. В боковой панели Explorer-а открыт **PRD §API.4** + **Security Spec §2.3 · «Что не должно утекать в публичную surface»**.',
    '4. Прочитай оба документа. PRD §API.4 определяет публичный контракт. Security Spec §2.3 описывает поля, которые НЕ должны быть в публичных endpoint-ах (admin-only, moderation, internal user data).',
    '5. Сравни response с обеими спеками. Найди поле, которое возвращается в публичный response, но указано в Security Spec §2.3 как admin-only.',
    '6. Кликни на это поле в JSON viewer.',
    '',
    '**Что важно.** Это ваш первый security-related баг в курсе. Утечка внутренних полей — типичная категория security уязвимостей. Поле может быть «безобидным» на вид (просто массив тегов), но если оно используется для внутренних бизнес-процессов — у конкурентов или плохих игроков появляется новая surface для атаки. Правило: публичные endpoint-ы возвращают только то, что в публичном контракте. Всё остальное — security ошибка.',
  ].join('\n'),
  bugs: [B2_API_04_BUG],
  hints: [
    {
      text: 'PRD §API.4 описывает публичные поля: id, title, artistId, artistAvatar, thumbnailUrl, priceRub, createdAt. Что в response сверх этого списка?',
    },
    {
      text: 'Глянь Security Spec §2.3. Там список полей, помеченных как «admin-only · не отдавать публично». Какое из них всё-таки появилось в response публичного endpoint?',
    },
    {
      text: 'Поле `moderationFlags` (массив с moderation тегами типа `["pending-review", "user-reported-spam"]`) — admin-only по Security Spec §2.3, но возвращается публично. Это утечка. Кликни на это поле в JSON viewer.',
    },
  ],
  scoring: { baseScore: 7, firstAttemptBonus: 1, hintPenalty: 1, maxAttempts: 3 },
  achievementTriggers: [
    'detective-junior',
    'sprint-09-required',
    'api-master',
    'security-watcher',
  ],
  estimateMin: 18,
  skills: { API: 2, 'Practice Lab': 5, 'Requirements Analysis': 2, Security: 3 },
  requires: ['msw-api'],
};

// ─── B2-API-05 · S09 · wrong-shape (nested vs flat structure) ────────────────

const B2_API_05_BUG: ApiContractViolationBug = {
  id: 'B1',
  kind: 'api-contract-violation',
  endpoint: 'GET /api/artists/:id/portfolio',
  hotspot: '[data-api-response-field="artworks"]',
  expectedShape:
    'artworks: Array<Artwork> (плоский массив объектов работ)',
  actualResponse:
    'artworks: { items: Array<Artwork>, count: number, page: number } (объект с обёрткой pagination, но клиент ждёт плоский массив)',
  violationType: 'wrong-shape',
  violationField: 'artworks',
  authorNote:
    'API сменил структуру с flat array на pagination wrapper в новом релизе backend, но контракт PRD §API.7 не обновлён, и frontend не адаптирован. Client читает response.artworks ожидая массив, получает объект. UI показывает «0 работ» (потому что Array.isArray(artworks) = false для объекта). Pedagogical hook: типовой breaking change → versioning issue. Hotspot = artworks field в response viewer.',
};

const B2_API_05: Scenario = {
  id: 'B2-API-05',
  sprint: 9,
  tier: 'bonus',
  mode: 'investigate',
  rank: 'api-master-junior',
  startRoute: '/artist/lera',
  mountStrategy: 'iframe-route',
  title: 'API контракт · структура response сменилась',
  brief:
    'Все портфолио на Atelier показывают «0 работ», хотя у художников десятки работ. Серж: «Backend выкатил новый релиз вчера. Что-то с shape response».',
  taskMd: [
    '**Контекст.** Утром после нового backend деплоя поддержка получила 30 жалоб «портфолио пустое». Лера, Аня, остальные художники — у всех «0 работ» на странице. В БД работы есть, листинги работают. Проблема — в API.',
    '',
    '**Что делать.**',
    '1. Открой `/artist/lera` (любого художника). Должно быть много работ — увидишь «0 работ».',
    '2. Открой **🌐 API** Explorer. Endpoint `GET /api/artists/lera/portfolio` уже вызван.',
    '3. Глянь response. Особенно поле `artworks` — какая у него структура?',
    '4. Открой **PRD §API.7** в боковой панели — какой shape поля `artworks` указан в контракте?',
    '5. Найди расхождение. Кликни на поле `artworks` в response viewer.',
    '',
    '**Что важно.** Это типовой случай **breaking change без версионирования API**. Backend сменил структуру (добавил pagination wrapper), но контракт PRD не обновили, и frontend по-старому ждёт плоский массив. Когда frontend читает `response.artworks` — получает объект вместо массива. `Array.isArray(...)` возвращает `false` → UI показывает empty state. Урок: любое изменение shape — это breaking change, нужен или новый endpoint version, или одновременный update frontend + контракта.',
  ].join('\n'),
  bugs: [B2_API_05_BUG],
  hints: [
    {
      text: 'Frontend ждёт `artworks` как массив. А что прилетело в response? Глянь response viewer — там может быть объект вместо массива.',
    },
    {
      text: 'Backend завернул artworks в pagination wrapper: `{ items: [...], count: N, page: 1 }`. Но контракт PRD §API.7 указывает `artworks: Array<Artwork>` — плоский массив без обёртки.',
    },
    {
      text: 'Поле `artworks` приходит как объект `{ items, count, page }`, но контракт требует плоский массив. Это wrong-shape violation. Кликни на поле `artworks` в response viewer (там подсветка type indicator: object вместо array).',
    },
  ],
  scoring: { baseScore: 7, firstAttemptBonus: 1, hintPenalty: 1, maxAttempts: 3 },
  achievementTriggers: [
    'detective-junior',
    'sprint-09-bonus',
    'api-master',
    'curious-cat',
  ],
  estimateMin: 18,
  skills: { API: 3, 'Practice Lab': 5, 'Requirements Analysis': 2 },
  requires: ['msw-api'],
};

// ─── B2-API-06 · S09 · Author mode · write API request from spec ─────────────

const B2_API_06: Scenario = {
  id: 'B2-API-06',
  sprint: 9,
  tier: 'required',
  mode: 'author',
  rank: 'author-apprentice',
  startRoute: '/api-explorer',
  mountStrategy: 'iframe-route',
  title: 'Сформируй POST /api/cart/add по контракту',
  brief:
    'Серж сбросил тебе spec для POST /api/cart/add. Сформируй валидный request (URL, headers, body) — потом мы запустим его через API Explorer и проверим ответ.',
  taskMd: [
    '**Контекст.** Серж дал тебе работу: «Нужно проверить руками что endpoint `POST /api/cart/add` ведёт себя по контракту в трёх случаях — успешный сценарий, проданная работа, невалидный ввод. Перед тем как тестить, сформулируй три валидных request. Я хочу видеть что ты понимаешь как из спеки получается реальный HTTP request».',
    '',
    '**Что делать.** В textarea ниже напиши **3 раздельных request** для трёх кейсов:',
    '',
    '**1. Успешный сценарий.** Добавляем 1 единицу доступной работы `art_lera_014` для авторизованной пользовательницы `usr_anya_22` (token `Bearer xyz123`).',
    '',
    '**2. Проданная работа.** Добавляем 1 единицу проданной работы `art_lera_007`. Та же пользовательница.',
    '',
    '**3. Невалидный ввод.** Пользовательница пытается добавить **0 единиц** работы (qty=0 — нарушение контракта, qty должно быть ≥ 1).',
    '',
    '**Формат каждого request (всё что нужно для запуска через curl или API Explorer):**',
    '```',
    'METHOD URL',
    'Header-Name: Header-Value',
    'Header-Name: Header-Value',
    '',
    '{ body JSON }',
    '```',
    '',
    '**Подсказки по контракту (PRD §API.5):**',
    '- Endpoint: `POST /api/cart/add`',
    '- Auth: `Authorization: Bearer <token>` обязательно',
    '- Content-Type: `application/json`',
    '- Body schema: `{ artworkId: string, qty: number (≥1) }`',
    '- Ожидаемые статусы: 200 OK (успех), 400 Bad Request (невалидный ввод), 409 Conflict (распродана)',
    '',
    '**Что важно.** В этом сценарии ты работаешь не с готовым багом — ты сам конструируешь HTTP-request из спеки. Это базовый навык API testing: прочитать контракт, понять что должно лететь в request, сложить корректный HTTP вызов. Каждый из 3 request будет тестировать **отдельный путь** в API: success, business-rule rejection, validation rejection.',
  ].join('\n'),
  rubric: {
    items: [
      { id: 'R1', text: 'Все 3 request присутствуют и помечены (1, 2, 3)' },
      { id: 'R2', text: 'METHOD = POST, URL = /api/cart/add — во всех трёх' },
      {
        id: 'R3',
        text: 'Header `Authorization: Bearer xyz123` присутствует во всех трёх (auth обязателен)',
      },
      {
        id: 'R4',
        text: 'Header `Content-Type: application/json` присутствует во всех трёх',
      },
      {
        id: 'R5',
        text: 'Body request 1 валиден: `{"artworkId": "art_lera_014", "qty": 1}` (или эквивалент с правильным artworkId + qty ≥ 1)',
      },
      {
        id: 'R6',
        text: 'Body request 2 использует `artworkId: "art_lera_007"` (проданная работа · сам request валиден, ответ ожидаем 409)',
      },
      {
        id: 'R7',
        text: 'Body request 3 использует `qty: 0` (для проверки 400 Bad Request)',
      },
      {
        id: 'R8',
        text: 'Bonus: студент явно указал ожидаемый status code для каждого case (200 / 409 / 400)',
      },
    ],
    passThreshold: 6,
  },
  sampleAnswer: [
    '**1. Happy path:**',
    '```',
    'POST /api/cart/add',
    'Authorization: Bearer xyz123',
    'Content-Type: application/json',
    '',
    '{ "artworkId": "art_lera_014", "qty": 1 }',
    '```',
    'Ожидаемый ответ: `200 OK` + body `{ "success": true, "cartId": "..." }`',
    '',
    '**2. Sold-out:**',
    '```',
    'POST /api/cart/add',
    'Authorization: Bearer xyz123',
    'Content-Type: application/json',
    '',
    '{ "artworkId": "art_lera_007", "qty": 1 }',
    '```',
    'Ожидаемый ответ: `409 Conflict` + body `{ "error": "out_of_stock" }`',
    '',
    '**3. Invalid input:**',
    '```',
    'POST /api/cart/add',
    'Authorization: Bearer xyz123',
    'Content-Type: application/json',
    '',
    '{ "artworkId": "art_lera_014", "qty": 0 }',
    '```',
    'Ожидаемый ответ: `400 Bad Request` + body `{ "error": "qty_must_be_positive" }`',
  ].join('\n'),
  hints: [
    {
      text: 'HTTP request состоит из 4 частей: первая строка = METHOD + URL, потом headers (каждый на своей строке), пустая строка, потом body. Вернись к спеке: что метод? что URL? какие headers требуются?',
    },
    {
      text: 'Headers: Authorization (для auth) + Content-Type: application/json (раз body — JSON). Body: для каждого case разный artworkId и/или qty.',
    },
    {
      text: 'Отличие 3 cases только в body (artworkId или qty). Метод/URL/headers одинаковые. Sample answer покажет точную форму после твоего ответа.',
    },
  ],
  scoring: { baseScore: 8, firstAttemptBonus: 0, hintPenalty: 1, maxAttempts: 3 },
  achievementTriggers: [
    'author-junior',
    'sprint-09-required',
    'api-master',
  ],
  estimateMin: 20,
  skills: { API: 4, 'Practice Lab': 5, 'Requirements Analysis': 2, Documentation: 1 },
  requires: ['msw-api'],
};

// ────────────────────────────────────────────────────────────────────────────
// Sprint 10 · Postman MASTER — B2-API-07..12
// Pattern shift S09 → S10: bug-injection ↓, Author-mode ↑.
// Студент строит свои collection / scripts / assertions из спеки.
// Один Investigate (B2-API-11 debug-broken-assertions) через customModule.
// ────────────────────────────────────────────────────────────────────────────

// ─── B2-API-07 · S10 · Build Postman collection (cart flow · 4 endpoints) ───

const B2_API_07: Scenario = {
  id: 'B2-API-07',
  sprint: 10,
  tier: 'required',
  mode: 'author',
  rank: 'author-apprentice',
  startRoute: '/postman',
  mountStrategy: 'iframe-route',
  title: 'Собери первую Postman collection — cart flow',
  brief:
    'Серж: «Покрываем cart flow API-тестами. Собери collection из 4 endpoints — список, добавление, удаление, checkout. Я отрэвьюю».',
  taskMd: [
    '**Контекст.** Atelier выкатывает v1.0 через 2 недели. Серж распределил тестовое покрытие — тебе достался cart flow. Нужно собрать первую внутреннюю Postman collection: 4 request, организованные так, чтобы любой member команды мог запустить и понять, что к чему.',
    '',
    '**Что делать.** Открой Postman UI (`/postman` route в Atelier). Создай новую collection «Cart Flow · Atelier» с 4 requests:',
    '',
    '1. **GET cart** — `GET /api/cart` — посмотреть содержимое корзины',
    '2. **Add to cart** — `POST /api/cart/add` — добавить работу',
    '3. **Remove from cart** — `DELETE /api/cart/{itemId}` — удалить позицию',
    '4. **Checkout** — `POST /api/cart/checkout` — оформить заказ',
    '',
    '**По спеке (PRD §API.5):**',
    '- Все requests требуют `Authorization: Bearer <token>` (collection-level auth OK)',
    '- POST/DELETE требуют `Content-Type: application/json`',
    '- Тестовые данные: `{ artworkId: "art_lera_014", qty: 1 }`',
    '- Auth token для тестов: `Bearer test_token_xyz`',
    '',
    '**Что важно.** Collection — это не просто список requests. Это **документация API + готовая среда для тестирования**. Хорошая collection: понятная новому человеку без объяснений. Плохая collection — это 20 requests с именами `request1`, `request2` и hardcoded токенами в каждом.',
  ].join('\n'),
  rubric: {
    items: [
      { id: 'R1', text: 'Collection содержит 4 requests, покрывающие все 4 endpoints' },
      { id: 'R2', text: 'Каждый request имеет правильный METHOD и URL' },
      {
        id: 'R3',
        text: 'Authorization header настроен (либо collection-level auth, либо per-request)',
      },
      {
        id: 'R4',
        text: 'POST/DELETE requests имеют Content-Type: application/json',
      },
      {
        id: 'R5',
        text: 'Body для POST /api/cart/add содержит валидный JSON с artworkId + qty',
      },
      {
        id: 'R6',
        text: 'Имена requests описательные (не «request1», а «Add to cart» или подобное)',
      },
      {
        id: 'R7',
        text: 'Bonus: Collection имеет description, объясняющий назначение',
      },
    ],
    passThreshold: 5,
  },
  sampleAnswer: [
    '**Collection: «Cart Flow · Atelier»**',
    'Description: «Полное покрытие cart endpoint surface для Atelier v1.0. Перед запуском заполнить env var `authToken`».',
    'Auth (collection-level): Bearer Token = `{{authToken}}`',
    '',
    '**1. GET cart**',
    '```',
    'GET {{baseUrl}}/api/cart',
    '```',
    '',
    '**2. Add to cart**',
    '```',
    'POST {{baseUrl}}/api/cart/add',
    'Content-Type: application/json',
    '',
    '{ "artworkId": "art_lera_014", "qty": 1 }',
    '```',
    '',
    '**3. Remove from cart**',
    '```',
    'DELETE {{baseUrl}}/api/cart/{{itemId}}',
    '```',
    '',
    '**4. Checkout**',
    '```',
    'POST {{baseUrl}}/api/cart/checkout',
    'Content-Type: application/json',
    '',
    '{ "paymentMethod": "card", "shippingAddressId": "addr_1" }',
    '```',
  ].join('\n'),
  hints: [
    {
      text: 'Постман разделяет requests по folders. Создай folder «Cart Flow», внутри — 4 requests с описательными именами. Auth можно вынести на уровень collection.',
    },
    {
      text: 'Collection-level auth = одна настройка для всех requests внутри. Если каждый request копирует Authorization header — это сигнал, что нужно вынести наверх.',
    },
    {
      text: 'Для POST: METHOD + URL + Content-Type header + JSON body. Для DELETE: METHOD + URL + path параметр через {{itemId}}. Для GET: только METHOD + URL.',
    },
  ],
  scoring: { baseScore: 8, firstAttemptBonus: 0, hintPenalty: 1, maxAttempts: 3 },
  achievementTriggers: [
    'author-junior',
    'sprint-10-required',
    'api-master',
    'workspace-tools',
  ],
  estimateMin: 22,
  skills: { API: 4, 'Practice Lab': 5, 'Workspace Tools': 3, Documentation: 2 },
  requires: ['msw-api', 'postman-ui'],
};

// ─── B2-API-08 · S10 · Pre-request script для авторизации ────────────────────

const B2_API_08: Scenario = {
  id: 'B2-API-08',
  sprint: 10,
  tier: 'required',
  mode: 'author',
  rank: 'author-apprentice',
  startRoute: '/postman',
  mountStrategy: 'iframe-route',
  title: 'Pre-request script · автоматическое получение токена',
  brief:
    'Atelier auth токен expires через 5 минут. Постоянно копировать новый — не вариант. Напиши pre-request script, который сам обновляет токен перед каждым request.',
  taskMd: [
    '**Контекст.** Серж: «Я запускаю свою collection раз в час, постоянно вижу 401 — токен expires быстрее, чем я успеваю кликать. Напиши pre-request script, который сам идёт за свежим токеном перед каждым request, кладёт его в env var, и main request использует уже свежий».',
    '',
    '**Что делать.** В Postman UI открой свой request на `GET /api/cart` (из B2-API-07). Перейди во вкладку **Pre-request Script**. Напиши JavaScript-ish код, который:',
    '',
    '1. Делает POST request на `/api/auth/token` с body `{ "username": "test_user", "password": "test_password" }`.',
    '2. Из response достаёт поле `token`.',
    '3. Сохраняет токен в environment variable `authToken`.',
    '4. Main request использует `{{authToken}}` в `Authorization: Bearer {{authToken}}`.',
    '',
    '**Postman API подсказки:**',
    '- `pm.sendRequest(requestConfig, callback)` — отправить request из script',
    '- `pm.environment.set("varName", value)` — сохранить значение в env',
    '- `pm.environment.get("varName")` — прочитать значение',
    '- callback signature: `(err, response)` — стандартный node-style',
    '',
    '**Что важно.** Pre-request scripts превращают Postman из «папки requests» в **программируемый тестовый рантайм**. Можно делать setup (получить токен / создать тестовые данные / dependent requests), cleanup (логи / удалить созданное), conditional logic (skip если env != prod). Это первый шаг к real automation за пределами Postman.',
  ].join('\n'),
  rubric: {
    items: [
      {
        id: 'R1',
        text: 'Script делает request на /api/auth/token endpoint (POST)',
      },
      {
        id: 'R2',
        text: 'Из response достаётся поле token (через response.json().token или эквивалент)',
      },
      {
        id: 'R3',
        text: 'Token сохраняется в env var через pm.environment.set("authToken", ...)',
      },
      {
        id: 'R4',
        text: 'Используется pm.sendRequest({...}, callback) async pattern',
      },
      {
        id: 'R5',
        text: 'Main request использует {{authToken}} в Authorization header',
      },
      {
        id: 'R6',
        text: 'Bonus: Handles error case (если token endpoint вернул не-200 / нет token field)',
      },
    ],
    passThreshold: 4,
  },
  sampleAnswer: [
    '**Pre-request Script:**',
    '```javascript',
    'pm.sendRequest({',
    '  url: pm.environment.get("baseUrl") + "/api/auth/token",',
    '  method: "POST",',
    '  header: { "Content-Type": "application/json" },',
    '  body: {',
    '    mode: "raw",',
    '    raw: JSON.stringify({ username: "test_user", password: "test_password" })',
    '  }',
    '}, function (err, response) {',
    '  if (err || response.code !== 200) {',
    '    console.error("Auth failed:", err || response.code);',
    '    return;',
    '  }',
    '  const token = response.json().token;',
    '  pm.environment.set("authToken", token);',
    '});',
    '```',
    '',
    '**Main request Authorization header:**',
    '```',
    'Authorization: Bearer {{authToken}}',
    '```',
  ].join('\n'),
  hints: [
    {
      text: 'pm.sendRequest принимает 2 аргумента: config object (url/method/header/body) и callback (err, response). Структура callback стандартная node-style.',
    },
    {
      text: 'Чтобы достать поле из JSON response: `response.json().fieldName`. Чтобы записать в env: `pm.environment.set("name", value)`. Main request увидит {{name}} как переменную.',
    },
    {
      text: 'Полный flow: pre-request делает auth call → достаёт token → пишет в env → main request читает {{authToken}} в header. Если auth call упадёт — main request пойдёт со старым/пустым token (отсюда нужен error handling).',
    },
  ],
  scoring: { baseScore: 9, firstAttemptBonus: 1, hintPenalty: 1, maxAttempts: 3 },
  achievementTriggers: [
    'author-junior',
    'sprint-10-required',
    'api-master',
    'workspace-tools',
  ],
  estimateMin: 25,
  skills: { API: 4, 'Practice Lab': 5, 'Workspace Tools': 4, 'Test Design': 1 },
  requires: ['msw-api', 'postman-ui', 'auth-flow'],
};

// ─── B2-API-09 · S10 · Environment variables (dev/staging/prod) ──────────────

const B2_API_09: Scenario = {
  id: 'B2-API-09',
  sprint: 10,
  tier: 'required',
  mode: 'author',
  rank: 'author-apprentice',
  startRoute: '/postman',
  mountStrategy: 'iframe-route',
  title: 'Environments · одна collection, три окружения',
  brief:
    'Серж: «Нашу cart-flow collection я хочу гонять и в dev, и в staging, и в prod без правки requests. Сделай 3 environment-конфига и подключи переменные».',
  taskMd: [
    '**Контекст.** Сейчас твоя collection (из B2-API-07) hardcoded на dev URL и dev token. Нужно отвязаться от конкретной среды — при переключении dev/staging/prod requests должны автоматически попадать на нужный baseUrl + использовать правильный auth.',
    '',
    '**Что делать.** В Postman UI открой Environments. Создай 3 environment-конфига:',
    '',
    '**dev:**',
    '- `baseUrl` = `https://dev-atelier.local`',
    '- `authToken` = `dev_test_xyz` (тестовый токен · не secret)',
    '',
    '**staging:**',
    '- `baseUrl` = `https://staging.atelier.io`',
    '- `authToken` = `stg_test_abc` (тестовый токен · не secret)',
    '',
    '**prod:**',
    '- `baseUrl` = `https://atelier.io`',
    '- `authToken` = `<placeholder>` (НЕ хардкодить prod-токен · оставить пустым / secret type · команда заполняет вручную)',
    '',
    'Затем обнови все requests в твоей collection: hardcoded URL (`https://dev-atelier.local/api/cart`) → переменная (`{{baseUrl}}/api/cart`). Header `Authorization: Bearer dev_test_xyz` → `Authorization: Bearer {{authToken}}`.',
    '',
    '**Что важно.** Environments — это **способ изолировать среду от collection**. Один из золотых принципов API testing: **никогда не хардкодить prod-credentials в shared collection**. Если ты push\'нул collection в репозиторий с prod-токеном внутри — это инцидент. Postman umely: env vars типа `secret` маскируются в UI и не экспортируются вместе с collection.',
  ].join('\n'),
  rubric: {
    items: [
      { id: 'R1', text: 'Создано 3 environments: dev, staging, prod' },
      {
        id: 'R2',
        text: 'dev environment: baseUrl=https://dev-atelier.local, authToken=dev_test_xyz',
      },
      {
        id: 'R3',
        text: 'staging environment: baseUrl=https://staging.atelier.io, authToken=stg_test_abc',
      },
      {
        id: 'R4',
        text: 'prod environment: baseUrl=https://atelier.io, authToken помечен как secret или placeholder (НЕ hardcoded prod-токен)',
      },
      {
        id: 'R5',
        text: 'Все requests в collection используют {{baseUrl}} вместо hardcoded URL',
      },
      {
        id: 'R6',
        text: 'Все requests используют {{authToken}} в Authorization header вместо hardcoded',
      },
      {
        id: 'R7',
        text: 'Bonus: Студент явно отметил, почему prod-токен не должен попадать в shared collection',
      },
    ],
    passThreshold: 5,
  },
  sampleAnswer: [
    '**Environments:**',
    '',
    '| Variable | dev | staging | prod |',
    '|---|---|---|---|',
    '| baseUrl | https://dev-atelier.local | https://staging.atelier.io | https://atelier.io |',
    '| authToken | dev_test_xyz | stg_test_abc | (empty · secret type) |',
    '',
    '**Updated request example (Add to cart):**',
    '```',
    'POST {{baseUrl}}/api/cart/add',
    'Authorization: Bearer {{authToken}}',
    'Content-Type: application/json',
    '',
    '{ "artworkId": "art_lera_014", "qty": 1 }',
    '```',
    '',
    '**Почему prod-token не хардкодим:**',
    'Prod-токен даёт доступ к боевым данным реальных пользователей. Если такая collection окажется в общем репозитории, GitHub leaks-сканеры или внутренний рейд-аудит зафиксируют утечку credentials. Стандарт: prod-credentials хранятся в personal env (на машине разработчика) или в secret manager (1Password, Vault), и в shared collection остаётся только placeholder.',
  ].join('\n'),
  hints: [
    {
      text: 'В Postman UI вверху справа есть dropdown «Environment». Создай 3 разных env через «+ New Environment». В каждом — список переменных (key/value/type).',
    },
    {
      text: 'Переменная в request пишется как `{{varName}}`. Postman подставит значение из active environment. Если active = staging, то `{{baseUrl}}` → `https://staging.atelier.io`.',
    },
    {
      text: 'Для prod-токена выбери тип «secret» (не «default»). Postman будет маскировать значение в UI и не включит в export. Так prod-credentials не утекут с shared collection.',
    },
  ],
  scoring: { baseScore: 8, firstAttemptBonus: 1, hintPenalty: 1, maxAttempts: 3 },
  achievementTriggers: [
    'author-junior',
    'sprint-10-required',
    'api-master',
    'workspace-tools',
    'security-watcher',
  ],
  estimateMin: 18,
  skills: {
    API: 3,
    'Practice Lab': 5,
    'Workspace Tools': 4,
    Security: 2,
    Documentation: 1,
  },
  requires: ['msw-api', 'postman-ui'],
};

// ─── B2-API-10 · S10 · Test assertions для контракта ─────────────────────────

const B2_API_10: Scenario = {
  id: 'B2-API-10',
  sprint: 10,
  tier: 'required',
  mode: 'author',
  rank: 'author-apprentice',
  startRoute: '/postman',
  mountStrategy: 'iframe-route',
  title: 'Тесты для GET /api/artworks/recent',
  brief:
    'Серж: «У нас есть `GET /api/artworks/recent` — пиши на него 4 теста: статус, тип ответа, обязательные поля, отсутствие утечки PII».',
  taskMd: [
    '**Контекст.** Endpoint `GET /api/artworks/recent` возвращает массив artwork-объектов. Сейчас на нём 0 тестов — любой breaking change в backend пройдёт незамеченным. Серж: «Покрой контракт минимальным sanity-suite, 4 теста».',
    '',
    '**Что делать.** Открой свой request на `GET /api/artworks/recent` (из collection B2-API-07). Перейди во вкладку **Tests**. Напиши 4 named test через `pm.test(name, fn)`:',
    '',
    '**Test 1.** Status code = 200.',
    '',
    '**Test 2.** Response body — массив (не объект, не null).',
    '',
    '**Test 3.** Каждый объект массива содержит обязательные поля: `id`, `title`, `artistId`, `artistAvatar`, `thumbnailUrl`, `priceRub`, `createdAt`. (По PRD §API.4.)',
    '',
    '**Test 4.** Никаких PII полей не утекает: `email`, `phoneNumber`, `internalUserHash`, `moderationFlags` — этих полей не должно быть нигде в response.',
    '',
    '**Postman test API подсказки:**',
    '- `pm.response.code` — статус-код (number)',
    '- `pm.response.json()` — распарсенный body',
    '- `pm.expect(actual).to.eql(expected)` или `to.have.status(...)` — assertions (chai-style)',
    '- `Array.isArray(...)` — проверка типа массива',
    '- `forEach` для итерации по массиву объектов',
    '',
    '**Что важно.** Tests в Postman — это **первая линия обороны контракта**. Они дешёвые (написал один раз — гоняешь сколько угодно), быстрые (<1с на response), фиксируют поломки **до** того как они доходят до frontend. Контракт = форма (status + shape) + ограничения (PII никогда не утекает). Минимальный sanity-suite на endpoint — 3-5 тестов.',
  ].join('\n'),
  rubric: {
    items: [
      { id: 'R1', text: '4 named tests присутствуют (через pm.test или эквивалент)' },
      {
        id: 'R2',
        text: 'Test 1: проверка status code = 200 (через pm.response.code или to.have.status)',
      },
      {
        id: 'R3',
        text: 'Test 2: проверка что body — массив (через Array.isArray или эквивалент)',
      },
      {
        id: 'R4',
        text: 'Test 3: проверка обязательных полей (через forEach + pm.expect на каждом объекте)',
      },
      {
        id: 'R5',
        text: 'Test 4: проверка отсутствия PII полей в каждом объекте (forbidden field list)',
      },
      {
        id: 'R6',
        text: 'Bonus: имена tests описательные («GET /recent · status 200» лучше чем «test 1»)',
      },
      {
        id: 'R7',
        text: 'Bonus: использование .to.have.all.keys или .to.include.all.keys для shape проверки',
      },
    ],
    passThreshold: 5,
  },
  sampleAnswer: [
    '**Tests tab:**',
    '```javascript',
    'const REQUIRED_FIELDS = ["id", "title", "artistId", "artistAvatar", "thumbnailUrl", "priceRub", "createdAt"];',
    'const FORBIDDEN_FIELDS = ["email", "phoneNumber", "internalUserHash", "moderationFlags"];',
    '',
    'pm.test("GET /recent · status 200", function () {',
    '  pm.expect(pm.response.code).to.eql(200);',
    '});',
    '',
    'pm.test("GET /recent · response is array", function () {',
    '  pm.expect(Array.isArray(pm.response.json())).to.be.true;',
    '});',
    '',
    'pm.test("GET /recent · каждый artwork содержит обязательные поля", function () {',
    '  pm.response.json().forEach((artwork, i) => {',
    '    REQUIRED_FIELDS.forEach(field => {',
    '      pm.expect(artwork, `artwork[${i}] missing ${field}`).to.have.property(field);',
    '    });',
    '  });',
    '});',
    '',
    'pm.test("GET /recent · нет утечки PII", function () {',
    '  pm.response.json().forEach((artwork, i) => {',
    '    FORBIDDEN_FIELDS.forEach(field => {',
    '      pm.expect(artwork, `artwork[${i}] leaks ${field}`).to.not.have.property(field);',
    '    });',
    '  });',
    '});',
    '```',
  ].join('\n'),
  hints: [
    {
      text: 'Каждый pm.test(name, fn) — отдельный тест. Если ассерты внутри fn падают, тест помечается как failed. Имя теста должно описывать что именно проверяется.',
    },
    {
      text: 'Для проверки массива: Array.isArray(obj) === true. Для проверки набора полей в объекте: forEach + pm.expect(obj).to.have.property(...). Для отсутствия поля: .to.not.have.property(...).',
    },
    {
      text: 'Если тестов много похожих (проверять N полей × M объектов) — выноси список полей в const, делай вложенный forEach. Это делает тест расширяемым: добавил поле — добавил в const, всё остальное продолжает работать.',
    },
  ],
  scoring: { baseScore: 9, firstAttemptBonus: 1, hintPenalty: 1, maxAttempts: 3 },
  achievementTriggers: [
    'author-junior',
    'sprint-10-required',
    'api-master',
    'test-design',
  ],
  estimateMin: 22,
  skills: { API: 4, 'Practice Lab': 5, 'Test Design': 3, 'Workspace Tools': 2 },
  requires: ['msw-api', 'postman-ui'],
};

// ─── B2-API-11 · S10 · Investigate · Debug сломанные assertions (customModule)

const B2_API_11: Scenario = {
  id: 'B2-API-11',
  sprint: 10,
  tier: 'required',
  mode: 'investigate',
  rank: 'api-master-junior',
  startRoute: '/postman/debug-assertions',
  mountStrategy: 'iframe-route',
  customModule: 'block-2/postman-debug-assertions.tsx',
  title: 'Дебаг сломанной test suite',
  brief:
    'Серж дал collection с 5 assertions, 3 из которых «всегда зелёные», но на самом деле проверяют не то. Найди три сломанных. Кликай на тест в test runner UI.',
  taskMd: [
    '**Контекст.** В soak-test за выходные test suite показал 100% зелёных, но в понедельник пришёл реальный bug, который suite должен был поймать. Серж: «Один из тестов сломан, проверяет не то. Глянь suite — там 5 тестов. Найди те, у которых assertion-логика сломана».',
    '',
    '**Что делать.** Открой test runner в Postman UI (`/postman/debug-assertions`). Перед тобой collection с одним request `GET /api/artworks/recent` и 5 tests:',
    '',
    '1. **«Status is 200»** — проверяет статус код',
    '2. **«Body is array»** — проверяет что response body — массив',
    '3. **«Each item has price»** — проверяет что каждый artwork имеет priceRub',
    '4. **«Content-Type header present»** — проверяет наличие header',
    '5. **«Response time < 500ms»** — проверяет performance',
    '',
    'Все 5 показывают зелёный. Но 3 из них сломаны (логика assertion проверяет не то, что заявлено в имени). **Кликни на 3 сломанных в test runner UI** — это твой ответ.',
    '',
    '**Подсказка по типам ошибок (общие):** ошибки assertion обычно про (а) сравнение типов (string vs number), (б) частичная проверка (только первый элемент вместо всех), (в) использование функции, которая не делает то что кажется (`typeof []` ≠ `"array"`).',
    '',
    '**Что важно.** Зелёный assertion ≠ корректный assertion. Тест может быть «всегда зелёным», потому что проверяет что-то всегда истинное (например `typeof obj === "object"` — почти всегда true, но не имеет отношения к содержимому). Это категория багов в тестовом коде называется «false positive»: тест успокаивает, но не защищает. Защита от этого — code review tests, а не только code review production.',
  ].join('\n'),
  hints: [
    {
      text: 'Прочитай каждый assertion внимательно. Сравни его имя с тем, что код реально делает. Имя обещает одно, код может проверять другое.',
    },
    {
      text: 'Подозрительные паттерны: `typeof X === "array"` (typeof никогда не возвращает "array"), `pm.response.code === "200"` (число vs строка), `body[0].field` (только первый элемент, а в имени — «each item»).',
    },
    {
      text: 'Сломаны: (1) «Body is array» — использует `typeof === "array"` вместо `Array.isArray`. (2) «Status is 200» — сравнивает `pm.response.code === "200"` (string vs number, всегда false, но обёрнут в .to.not.eql и проходит). (3) «Each item has price» — проверяет только `[0]`. Кликни на эти три в UI.',
    },
  ],
  scoring: { baseScore: 9, firstAttemptBonus: 1, hintPenalty: 1, maxAttempts: 3 },
  achievementTriggers: [
    'detective-junior',
    'sprint-10-required',
    'api-master',
    'test-design',
  ],
  estimateMin: 22,
  skills: { API: 3, 'Practice Lab': 5, 'Test Design': 4, 'Workspace Tools': 2 },
  requires: ['msw-api', 'postman-ui'],
};

// ─── B2-API-12 · S10 · Bonus · Data-driven testing (CSV iteration) ───────────

const B2_API_12: Scenario = {
  id: 'B2-API-12',
  sprint: 10,
  tier: 'bonus',
  mode: 'author',
  rank: 'author-apprentice',
  startRoute: '/postman',
  mountStrategy: 'iframe-route',
  title: 'Data-driven testing · 6 кейсов из CSV',
  brief:
    'Один request, 6 разных входных данных, 6 разных ожидаемых статусов. Postman Runner умеет итерироваться по CSV. Собери runner-конфиг + CSV + assertion на статус.',
  taskMd: [
    '**Контекст.** Серж: «Тестируем `GET /api/users/{userId}` на разных состояниях пользователя. Активный → 200. Токен истёк → 401. Не существует → 404. Мягко удалён → 410. Слишком много запросов → 429. Триггер 500 → 500. Шесть кейсов, каждый — свой userId. Не хочу писать 6 requests руками — сделай через data-driven runner».',
    '',
    '**Что делать.** В Postman Runner-е есть **Data Files** — CSV/JSON файл с тестовыми данными, runner итерируется по строкам и каждый раз запускает request с новыми переменными.',
    '',
    '**1. Создай CSV файл** `users-test.csv` с 3 колонками:',
    '```',
    'testName,userId,expectedStatus',
    'happy_path,usr_anya_22,200',
    'expired_token,usr_expired_99,401',
    'nonexistent,usr_doesnt_exist,404',
    'soft_deleted,usr_deleted_55,410',
    'rate_limited,usr_too_many,429',
    'server_error,usr_trigger_500,500',
    '```',
    '',
    '**2. Создай request** на `GET {{baseUrl}}/api/users/{{userId}}`. `{{userId}}` будет подставляться из CSV на каждой итерации.',
    '',
    '**3. Напиши assertion** в Tests tab:',
    'Используй `pm.iterationData.get("expectedStatus")` чтобы достать ожидаемый статус-код для текущей итерации, и сравни с `pm.response.code`.',
    '',
    '**4. Запусти Runner**: load collection → load CSV file → нажми Run. Должно быть 6 итераций, каждая со своим результатом.',
    '',
    '**Что важно.** Data-driven testing — это **способ покрыть много кейсов одним кодом**. Вместо 6 одинаковых requests с разными userId — один request + CSV. Когда добавляется 7-й кейс — добавляешь строку в CSV, не трогаешь request. Это первый шаг к **parametrized testing**, который дальше живёт в JUnit (`@ParameterizedTest`), pytest (`@pytest.mark.parametrize`), и большинстве test runners.',
  ].join('\n'),
  rubric: {
    items: [
      {
        id: 'R1',
        text: 'CSV файл создан с header testName,userId,expectedStatus + 6 data rows',
      },
      {
        id: 'R2',
        text: 'Все 6 rows покрывают разные кейсы (status codes 200, 401, 404, 410, 429, 500)',
      },
      {
        id: 'R3',
        text: 'Request URL использует {{userId}} переменную (не hardcoded)',
      },
      {
        id: 'R4',
        text: 'Test использует pm.iterationData.get("expectedStatus") для динамической проверки',
      },
      {
        id: 'R5',
        text: 'Test сравнивает pm.response.code с expected status (через pm.expect или эквивалент)',
      },
      {
        id: 'R6',
        text: 'Bonus: parseInt при чтении из CSV (так как CSV даёт строку, а response.code — число)',
      },
      {
        id: 'R7',
        text: 'Bonus: тест использует testName из CSV в имени (для понятного report)',
      },
    ],
    passThreshold: 5,
  },
  sampleAnswer: [
    '**users-test.csv:**',
    '```',
    'testName,userId,expectedStatus',
    'happy_path,usr_anya_22,200',
    'expired_token,usr_expired_99,401',
    'nonexistent,usr_doesnt_exist,404',
    'soft_deleted,usr_deleted_55,410',
    'rate_limited,usr_too_many,429',
    'server_error,usr_trigger_500,500',
    '```',
    '',
    '**Request:**',
    '```',
    'GET {{baseUrl}}/api/users/{{userId}}',
    'Authorization: Bearer {{authToken}}',
    '```',
    '',
    '**Tests tab:**',
    '```javascript',
    'const expected = parseInt(pm.iterationData.get("expectedStatus"));',
    'const testName = pm.iterationData.get("testName");',
    '',
    'pm.test(`${testName} · expects ${expected}`, function () {',
    '  pm.expect(pm.response.code).to.eql(expected);',
    '});',
    '```',
    '',
    '**Запуск:** Collection Runner → выбрать collection → загрузить users-test.csv → Run. 6 итераций, каждая зелёная если все статусы соответствуют.',
  ].join('\n'),
  hints: [
    {
      text: 'CSV должен иметь header в первой строке (имена колонок). Имена колонок становятся доступны через pm.iterationData.get("colName") внутри test/script.',
    },
    {
      text: 'Главный gotcha: CSV отдаёт значения как **строки**. expectedStatus = "200" (string), но pm.response.code = 200 (number). Без parseInt сравнение `=== "200"` всегда false. parseInt(...) = быстрый фикс.',
    },
    {
      text: 'Runner запускает collection N раз, где N = количество rows в CSV. На каждой итерации pm.iterationData.get(...) возвращает значения текущей строки. URL/headers/body тоже подставляют {{переменные}} из CSV.',
    },
  ],
  scoring: { baseScore: 10, firstAttemptBonus: 1, hintPenalty: 1, maxAttempts: 3 },
  achievementTriggers: [
    'author-junior',
    'sprint-10-bonus',
    'api-master',
    'test-design',
    'curious-cat',
  ],
  estimateMin: 25,
  skills: {
    API: 4,
    'Practice Lab': 5,
    'Test Design': 4,
    'Workspace Tools': 3,
    Documentation: 1,
  },
  requires: ['msw-api', 'postman-ui'],
};

// ────────────────────────────────────────────────────────────────────────────
// Sprint 08 · SQL basic — B2-SQL-01..05
// Pattern: SQL playground execute-and-verify через customModule (NO new TypedBug
// kind · per Genie ack 4 MAY · execute-verify ≠ bug-injection).
// One Author scenario (B2-SQL-05) с rubric для query writing skill.
// ────────────────────────────────────────────────────────────────────────────

// ─── B2-SQL-01 · S08 · Find orphan orders без payout entry ───────────────────

const B2_SQL_01: Scenario = {
  id: 'B2-SQL-01',
  sprint: 8,
  tier: 'required',
  mode: 'investigate',
  rank: 'sql-detective-junior',
  startRoute: '/sql-playground/orders-payouts',
  mountStrategy: 'iframe-route',
  customModule: 'block-2/sql-find-orphan-payouts.tsx',
  title: 'Найди осиротевшие заказы · расхождение по выплатам',
  brief:
    'Лера: «За последний месяц у меня продано 47 работ, выплата пришла только на 42. Где 5?» Серж: «Найди заказы Леры со статусом `paid`, у которых нет записи в `payouts`. SQL playground открыт.»',
  taskMd: [
    '**Контекст.** Лера написала в поддержку: «У меня в профиле показано 47 продаж за последний месяц, а на странице выплат — 42. Не понятно где остальные 5».',
    '',
    'Серж: «Это типовой кейс осиротевших записей: заказ создался, платёж прошёл, но процесс выплат по какой-то причине не создал запись в `payouts`. Найди пять таких заказов».',
    '',
    '**Что делать.** Открой **SQL Playground** (`/sql-playground/orders-payouts`). Слева — schema viewer (4 таблицы: `artists`, `artworks`, `orders`, `payouts`). Справа — query editor + result panel.',
    '',
    '1. Сначала проверь масштаб: посчитай `SELECT COUNT(*) FROM orders WHERE ...` для Леры со `status=\'paid\'` за последний месяц. Должно быть 47.',
    '2. Посчитай записи в `payouts` для тех же заказов. Должно быть 42.',
    '3. Напиши query, которая возвращает **только** те заказы, у которых **нет** выплаты. Hint: `LEFT JOIN ... WHERE p.id IS NULL`.',
    '4. Запусти query — должно быть 5 строк (осиротевшие заказы).',
    '5. Кликни на любую из 5 строк в результате — это твой ответ.',
    '',
    '**Schema reminder (в side-panel):**',
    '- `artists(id, username, display_name, ...)` — `artist_id` для Леры можно достать через `WHERE username=\'lera\'`',
    '- `orders(id, artist_id, artwork_id, status, total_rub, created_at)` — `status=\'paid\'`, последние 30 дней',
    '- `payouts(id, order_id, paid_at, amount_rub)` — связь с `orders` через `order_id`',
    '',
    '**Что важно.** Это первый pure-SQL сценарий курса. SQL — это **второй язык** для QA после JS/спеки. Ты не пишешь production-код, ты используешь SQL чтобы **исследовать данные** напрямую. `LEFT JOIN + IS NULL` — самый частый паттерн для поиска «осиротевших» строк (записи одной таблицы без связи во второй). Запомни этот паттерн — будешь использовать в каждом DB-расследовании.',
  ].join('\n'),
  hints: [
    {
      text: 'SQL для подсчёта: `SELECT COUNT(*) FROM orders WHERE artist_id = (SELECT id FROM artists WHERE username=\'lera\') AND status=\'paid\' AND created_at >= datetime(\'now\', \'-30 days\')`. Получишь 47. Аналогично для `payouts`.',
    },
    {
      text: 'LEFT JOIN сохраняет ВСЕ строки слева, даже если совпадения справа нет. Если совпадения нет — поля справа = NULL. Для «осиротевших» строк: `WHERE p.id IS NULL` отфильтровывает только те заказы, у которых выплата не нашлась.',
    },
    {
      text: 'Полная query: `SELECT o.id, o.created_at, o.total_rub FROM orders o LEFT JOIN payouts p ON p.order_id = o.id WHERE o.artist_id = (SELECT id FROM artists WHERE username=\'lera\') AND o.status=\'paid\' AND o.created_at >= datetime(\'now\', \'-30 days\') AND p.id IS NULL;` → 5 строк. Кликни любую.',
    },
  ],
  scoring: { baseScore: 8, firstAttemptBonus: 1, hintPenalty: 1, maxAttempts: 3 },
  achievementTriggers: [
    'detective-junior',
    'sprint-08-required',
    'sql-detective',
  ],
  estimateMin: 25,
  skills: { SQL: 5, 'Practice Lab': 5, 'Test Design': 1 },
  requires: ['sql-playground'],
};

// ─── B2-SQL-02 · S08 · NULL handling pitfall (COUNT field vs COUNT *) ────────

const B2_SQL_02: Scenario = {
  id: 'B2-SQL-02',
  sprint: 8,
  tier: 'required',
  mode: 'investigate',
  rank: 'sql-detective-junior',
  startRoute: '/sql-playground/artworks-count',
  mountStrategy: 'iframe-route',
  customModule: 'block-2/sql-null-counting.tsx',
  title: 'NULL подвох · отчёт по новым работам',
  brief:
    'Менеджер ставит KPI: 245 новых работ за месяц. Отчёт показывает 211. Серж: «Глянь query — там NULL-ловушка».',
  taskMd: [
    '**Контекст.** Менеджер: «У нас за последний месяц добавили 245 работ. Я подтверждаю по seed-аудиту. А SQL-отчёт говорит 211. Где 34?»',
    '',
    'Серж: «Не баг в данных. Баг в query. SQL имеет NULL-подвох в COUNT — кто-то на нём поскользнулся».',
    '',
    '**Что делать.** Открой SQL Playground. Перед тобой query, который возвращает 211 (но должен 245):',
    '',
    '```sql',
    'SELECT COUNT(category) AS new_artworks',
    'FROM artworks',
    'WHERE created_at >= datetime(\'now\', \'-30 days\');',
    '```',
    '',
    '1. Запусти query — увидишь `211`.',
    '2. Подсчитай по-другому: `SELECT COUNT(*) FROM artworks WHERE ...` (без `category`). Получишь `245`.',
    '3. Найди разницу: какие 34 работы не попали в первый query?',
    '4. Hint: SQL `COUNT(field)` имеет специальную семантику для NULL.',
    '5. Перепиши query так, чтобы вернуть `245`. Запусти — убедись.',
    '6. Кликни на текст результата (там будет «245») — это твой ответ.',
    '',
    '**Что важно.** В SQL `COUNT(field)` **не считает rows где field IS NULL**. `COUNT(*)` считает все rows. Это поведение по стандарту SQL и редко меняется. Если в данных есть NULL в поле, по которому делаешь COUNT — ты теряешь rows тихо. Это типовой источник «не сходящихся отчётов» в analytics. Правило: для подсчёта rows используй `COUNT(*)`. Для подсчёта непустых значений конкретного поля — `COUNT(field)` (явно).',
  ].join('\n'),
  hints: [
    {
      text: '`COUNT(category)` считает только те rows, где `category IS NOT NULL`. Если 34 работы добавили без категории (`category` = NULL) — они в подсчёт не попали.',
    },
    {
      text: 'Замени `COUNT(category)` на `COUNT(*)` — `COUNT(*)` считает строки независимо от NULL.',
    },
    {
      text: 'Правильная query: `SELECT COUNT(*) AS new_artworks FROM artworks WHERE created_at >= datetime(\'now\', \'-30 days\');` → возвращает 245. Это и есть ответ.',
    },
  ],
  scoring: { baseScore: 7, firstAttemptBonus: 1, hintPenalty: 1, maxAttempts: 3 },
  achievementTriggers: [
    'detective-junior',
    'sprint-08-required',
    'sql-detective',
  ],
  estimateMin: 18,
  skills: { SQL: 4, 'Practice Lab': 5, 'Requirements Analysis': 1 },
  requires: ['sql-playground'],
};

// ─── B2-SQL-03 · S08 · JOIN orphan records (deactivated artist artworks) ────

const B2_SQL_03: Scenario = {
  id: 'B2-SQL-03',
  sprint: 8,
  tier: 'required',
  mode: 'investigate',
  rank: 'sql-detective-junior',
  startRoute: '/sql-playground/artists-artworks',
  mountStrategy: 'iframe-route',
  customModule: 'block-2/sql-deactivated-artists.tsx',
  title: 'Сколько работ от деактивированных авторов в trending?',
  brief:
    'Серж в planning: «Деактивированные аккаунты должны быть hidden, а в trending feed их работы мелькают. Сколько таких? Глянь artworks JOIN artists на deactivated_at».',
  taskMd: [
    '**Контекст.** В подкасте про инциденты Серж: «Если кто-то deactivate\'нул аккаунт (через профиль settings → "удалить аккаунт"), его работы должны автоматически исчезнуть из публичного feed. Я вижу что они всё ещё там. Сколько именно?»',
    '',
    '**Что делать.** Открой SQL Playground. Доступны 2 таблицы:',
    '',
    '- `artists(id, username, deactivated_at)` — `deactivated_at` IS NOT NULL означает аккаунт деактивирован',
    '- `artworks(id, artist_id, status, ...)` — `status=\'published\'` означает работа в публичном feed',
    '',
    '1. Напиши query, которая считает количество **published artworks** от **деактивированных artists**.',
    '2. Запусти query — получишь число (это и есть «сколько утекает в trending»).',
    '3. Кликни на результат-cell (число) — это твой ответ.',
    '',
    '**Подсказка.** Тебе нужен JOIN `artists` ↔ `artworks` с двумя условиями: художник деактивирован AND работа опубликована.',
    '',
    '**Что важно.** Это **typical data integrity bug**: business rule «deactivated artists hidden» должно быть enforced в нескольких местах: (а) при rendering feed (frontend filter), (б) в API response (backend filter), (в) в сами данных (cron job, который перемещает status=hidden). Где-то один из этих trip wires не сработал. SQL playground даёт способ **измерить масштаб утечки** мгновенно — без ожидания деплоя или logs.',
  ].join('\n'),
  hints: [
    {
      text: 'JOIN syntax: `SELECT COUNT(*) FROM artworks aw JOIN artists a ON aw.artist_id = a.id WHERE ...`. Условие на двух столбцах: `a.deactivated_at IS NOT NULL` И `aw.status = \'published\'`.',
    },
    {
      text: '`IS NOT NULL` для проверки «deactivated_at заполнено» (то есть аккаунт реально деактивирован). И отдельно фильтр на `aw.status = \'published\'` (то есть работа в публичном feed).',
    },
    {
      text: 'Полная query: `SELECT COUNT(*) FROM artworks aw JOIN artists a ON aw.artist_id = a.id WHERE a.deactivated_at IS NOT NULL AND aw.status = \'published\';` → возвращает число (например 17). Это твой ответ — кликни на число в результате.',
    },
  ],
  scoring: { baseScore: 7, firstAttemptBonus: 1, hintPenalty: 1, maxAttempts: 3 },
  achievementTriggers: [
    'detective-junior',
    'sprint-08-required',
    'sql-detective',
  ],
  estimateMin: 22,
  skills: { SQL: 4, 'Practice Lab': 5, 'Test Design': 2 },
  requires: ['sql-playground'],
};

// ─── B2-SQL-04 · S08 · GROUP BY · refunded orders zero out artist revenue ───

const B2_SQL_04: Scenario = {
  id: 'B2-SQL-04',
  sprint: 8,
  tier: 'required',
  mode: 'investigate',
  rank: 'sql-detective-junior',
  startRoute: '/sql-playground/revenue-per-artist',
  mountStrategy: 'iframe-route',
  customModule: 'block-2/sql-revenue-aggregation.tsx',
  title: 'GROUP BY · художник с нулевым revenue',
  brief:
    'Финансовый отчёт revenue по артистам — у одного total=0, хотя продал 23 работы. Серж: «Глянь query, что-то с агрегацией».',
  taskMd: [
    '**Контекст.** Финансовый отдел построил отчёт revenue per artist:',
    '',
    '```sql',
    'SELECT a.username, SUM(o.total_rub) AS revenue_rub',
    'FROM artists a',
    'JOIN orders o ON o.artist_id = a.id',
    'WHERE o.status IN (\'paid\', \'refunded\')',
    '  AND o.created_at >= datetime(\'now\', \'-30 days\')',
    'GROUP BY a.username;',
    '```',
    '',
    'В отчёте у художника `lera` стоит `revenue_rub = 0`. Но по аудит-логу за последний месяц у Леры 23 заказа в статусе `paid` на общую сумму 67 400 ₽. Где revenue?',
    '',
    'Серж: «Какая-то ошибка либо в query, либо в данных. Найди».',
    '',
    '**Что делать.** Открой SQL Playground. Запусти исходный query, увидишь Lera = 0. Затем исследуй:',
    '',
    '1. Запусти `SELECT * FROM orders WHERE artist_id = (SELECT id FROM artists WHERE username=\'lera\') AND status IN (\'paid\', \'refunded\') AND created_at >= datetime(\'now\', \'-30 days\') ORDER BY total_rub;` — посмотри сами orders.',
    '2. Найди — какие `total_rub` значения там? Есть ли отрицательные?',
    '3. Если есть отрицательные — подумай: какой `status` у негативных? Что это означает в business-логике?',
    '4. Перепиши главный query так, чтобы revenue считался **только по paid orders** (не refunded). Запусти — Lera должна показать ~67 400 ₽.',
    '5. Кликни на cell с правильным revenue Леры — это твой ответ.',
    '',
    '**Что важно.** Refunded orders в этой системе хранятся как **отрицательные total_rub** (типичный паттерн в bookkeeping: refund = inverse transaction). Когда query суммирует `paid + refunded` — refunds **вычитают** из paid, и у художника, у которого refunds = paid, revenue = 0. Это category «aggregation bug»: query технически работает, но **бизнес-логика была интерпретирована неправильно** автором query. SQL не виноват — задача автора понимать схему данных и business rules перед написанием WHERE.',
  ].join('\n'),
  hints: [
    {
      text: 'У Леры много orders с total_rub отрицательным (например `-2 950`, `-3 100`). Какой у них status? Скорее всего «refunded». Что это значит для SUM?',
    },
    {
      text: 'Refund в этой системе = `total_rub: -X` со `status: "refunded"`. Когда суммируешь `paid + refunded` — refunds вычитают из paid. У Леры по совпадению paid сумма ≈ refund сумма → итог 0.',
    },
    {
      text: 'Фикс: убери refunded из WHERE. Стало `WHERE o.status = \'paid\'`. Запусти — Lera покажет ~67 400 ₽. Кликни на это число в результате.',
    },
  ],
  scoring: { baseScore: 8, firstAttemptBonus: 1, hintPenalty: 1, maxAttempts: 3 },
  achievementTriggers: [
    'detective-junior',
    'sprint-08-required',
    'sql-detective',
  ],
  estimateMin: 22,
  skills: { SQL: 5, 'Practice Lab': 5, 'Requirements Analysis': 2 },
  requires: ['sql-playground'],
};

// ─── B2-SQL-05 · S08 · Author · weekly metrics query ─────────────────────────

const B2_SQL_05: Scenario = {
  id: 'B2-SQL-05',
  sprint: 8,
  tier: 'required',
  mode: 'author',
  rank: 'author-apprentice',
  startRoute: '/sql-playground/author-weekly-metrics',
  mountStrategy: 'iframe-route',
  title: 'Напиши query для weekly metrics email',
  brief:
    'Серж: «Нужен query для еженедельного email менеджеру: перечень artists с ≥3 продажами за последние 7 дней. Имя, количество, сортировка по убыванию».',
  taskMd: [
    '**Контекст.** Менеджер каждую неделю получает email с топ-исполнителями: «Кто продал ≥3 работы за последнюю неделю». Сейчас email собирается руками — Серж хочет автоматизировать через cron + SQL query.',
    '',
    '**Что делать.** Напиши SQL query, которая:',
    '',
    '1. Возвращает artists, у которых **>=3 paid orders** за **last 7 days**.',
    '2. Колонки результата: `username` (artist), `display_name` (full name), `sales_count` (количество продаж).',
    '3. Сортировка: по `sales_count` **по убыванию**.',
    '4. Только **paid** orders в подсчёт (не refunded · не cancelled).',
    '',
    '**Schema reminder:**',
    '- `artists(id, username, display_name, ...)`',
    '- `artworks(id, artist_id, ...)` — связка artist ↔ artwork',
    '- `orders(id, artwork_id, status, created_at)` — связка artwork ↔ order через artwork_id',
    '',
    '**Особенности SQLite (наш playground использует sqlite-wasm):**',
    '- Last 7 days: `WHERE o.created_at >= datetime(\'now\', \'-7 days\')`',
    '- HAVING для filter после агрегации (NOT WHERE — WHERE не работает с агрегатами)',
    '',
    '**Напиши query в textarea ниже. Отправь — получишь rubric grade + sample answer.**',
    '',
    '**Что важно.** Это первая «full pipeline» query за курс: JOIN 3 таблиц + WHERE filter + GROUP BY + HAVING + ORDER BY. По такому шаблону строится 90% analytical queries в продакшене. Ключевые моменты: (а) join артистов через 2 таблицы (artists → artworks → orders), (б) фильтр времени правильным синтаксисом SQLite, (в) HAVING для фильтра после COUNT (нельзя фильтровать count в WHERE), (г) ORDER BY для presentability отчёта.',
  ].join('\n'),
  rubric: {
    items: [
      {
        id: 'R1',
        text: 'SELECT возвращает username, display_name, count (или эквиваленты)',
      },
      {
        id: 'R2',
        text: 'JOIN artists ↔ artworks ↔ orders (3-таблицы JOIN присутствует)',
      },
      {
        id: 'R3',
        text: 'WHERE filter по времени: created_at >= datetime(\'now\', \'-7 days\') или эквивалент',
      },
      {
        id: 'R4',
        text: 'WHERE filter по status = \'paid\' (исключает refunded/cancelled)',
      },
      { id: 'R5', text: 'GROUP BY по artist (минимум по a.id или a.username)' },
      { id: 'R6', text: 'HAVING COUNT(...) >= 3' },
      { id: 'R7', text: 'ORDER BY sales_count DESC (или эквивалент)' },
      {
        id: 'R8',
        text: 'Bonus: явный комментарий, почему именно \'paid\' (excludes refunded)',
      },
    ],
    passThreshold: 6,
  },
  sampleAnswer: [
    '```sql',
    '-- Топ artists с >=3 продажами за последние 7 дней.',
    '-- Только paid orders (refunded/cancelled не считаем).',
    'SELECT',
    '  a.username,',
    '  a.display_name,',
    '  COUNT(o.id) AS sales_count',
    'FROM artists a',
    'JOIN artworks aw ON aw.artist_id = a.id',
    'JOIN orders o ON o.artwork_id = aw.id',
    'WHERE o.created_at >= datetime(\'now\', \'-7 days\')',
    '  AND o.status = \'paid\'',
    'GROUP BY a.id, a.username, a.display_name',
    'HAVING COUNT(o.id) >= 3',
    'ORDER BY sales_count DESC;',
    '```',
    '',
    '**Почему `status = \'paid\'`, а не `status IN (\'paid\', \'refunded\')`:**',
    'В weekly email менеджеру нужны **успешные** продажи. Refunded — это отменённые сделки, для маркетинговых рассылок и метрик «топ-исполнители» refunded не считаются. Если включить refunded — рейтинг исказится: художник с одной paid + одной refunded покажется как «2 продажи», но реально продаж 0.',
    '',
    '**Ловушка: одинарные кавычки vs двойные.**',
    'Строка в SQL — это `\'paid\'`. Двойные кавычки — это **имя колонки**, а не строка. Коварство в том, что `status = "paid"` в SQLite всё равно сработает: движок сначала ищет колонку `paid`, не находит — и молча откатывается к строке. Тот же запрос в Postgres упадёт с `column "paid" does not exist`.',
    'Для QA это важнее, чем выглядит: запрос не падает, но переносимость теряется — проверка, зелёная на одной СУБД, ломается на другой. Пиши строки только в одинарных кавычках, двойные оставь именам колонок и таблиц.',
  ].join('\n'),
  hints: [
    {
      text: 'JOIN-цепочка: artists → artworks (через artist_id) → orders (через artwork_id). Это 3-таблицы JOIN. Старайся всегда писать алиасы (a, aw, o) — читается быстрее.',
    },
    {
      text: 'WHERE filter по времени для last 7 days: `o.created_at >= datetime(\'now\', \'-7 days\')`. Это SQLite-specific syntax. В Postgres было бы `o.created_at >= NOW() - INTERVAL \'7 days\'`. Помни какой dialect используешь.',
    },
    {
      text: 'HAVING vs WHERE: WHERE фильтрует rows ДО агрегации. HAVING фильтрует ПОСЛЕ. `HAVING COUNT(o.id) >= 3` означает «оставляем только artists у которых после GROUP BY count >= 3». В WHERE нельзя — там нет агрегатов.',
    },
  ],
  scoring: { baseScore: 10, firstAttemptBonus: 1, hintPenalty: 1, maxAttempts: 3 },
  achievementTriggers: [
    'author-junior',
    'sprint-08-required',
    'sql-detective',
  ],
  estimateMin: 25,
  skills: { SQL: 5, 'Practice Lab': 5, 'Documentation': 1 },
  requires: ['sql-playground'],
};

// ────────────────────────────────────────────────────────────────────────────
// Sprint 11 · SQL deep + БД — B2-SQL-06..10
// Pattern: subqueries · window functions · ER diagram · NoSQL toy · Faker spec.
// 4 Investigate (customModule) + 1 Author (rubric). Mode coverage extends S08.
// ────────────────────────────────────────────────────────────────────────────

// ─── B2-SQL-06 · S11 · Correlated subquery · price-vs-avg per artist ────────

const B2_SQL_06: Scenario = {
  id: 'B2-SQL-06',
  sprint: 11,
  tier: 'required',
  mode: 'investigate',
  rank: 'sql-detective-junior',
  startRoute: '/sql-playground/price-vs-avg',
  mountStrategy: 'iframe-route',
  customModule: 'block-2/sql-correlated-subquery.tsx',
  title: 'Correlated subquery · цена vs средняя по артисту',
  brief:
    'Найди работы, цена которых превышает среднюю цену **этого же** артиста более чем в 3 раза. Это outliers, которых трогает финансовый аудит.',
  taskMd: [
    '**Контекст.** Финансовый отдел проверяет ценообразование. Серж: «Если цена работы в 3+ раза выше средней цены **по тому же артисту** — это либо premium-релиз, либо ошибка ценообразования (например, в ценнике лишний ноль). Найди такие работы».',
    '',
    '**Что делать.** Открой SQL Playground. Тебе нужен **correlated subquery** — подзапрос, который зависит от текущей строки внешнего запроса.',
    '',
    '1. Каждый artwork имеет `artist_id` и `price_rub`.',
    '2. Для каждой работы посчитай среднюю цену по **тому же** artist (subquery).',
    '3. Верни artworks где `price_rub > 3 * avg_for_same_artist`.',
    '4. Запусти query — получишь N rows (вероятно 3-5).',
    '5. Кликни на любую row в результате — это твой ответ.',
    '',
    '**Шаблон correlated subquery:**',
    '```sql',
    'SELECT outer.col1, outer.col2',
    'FROM table outer',
    'WHERE outer.value > (',
    '  SELECT AGG_FN(inner.value)',
    '  FROM table inner',
    '  WHERE inner.fk = outer.fk  -- correlation на текущую row outer',
    ');',
    '```',
    '',
    '**Что важно.** «Correlated» означает что subquery referenцует колонку внешнего query (`inner.artist_id = outer.artist_id`). Subquery выполняется **per row** outer query — для каждой работы свой расчёт средней. Это мощный инструмент, но дорогой по performance (N×M времени). Альтернатива — JOIN с pre-aggregated subquery (faster для больших данных). Для investigation на dev playground correlated проще читается.',
  ].join('\n'),
  hints: [
    {
      text: 'Correlated subquery — это `WHERE outer.col > (SELECT AGG FROM table WHERE table.fk = outer.fk)`. Связь с outer через `WHERE inner.artist_id = outer.artist_id`.',
    },
    {
      text: 'AVG() для средней цены: `(SELECT AVG(price_rub) FROM artworks aw2 WHERE aw2.artist_id = aw.artist_id)`. Используй разные алиасы (aw vs aw2) чтобы не запутаться.',
    },
    {
      text: 'Полная query: `SELECT id, artist_id, title, price_rub FROM artworks aw WHERE price_rub > 3 * (SELECT AVG(price_rub) FROM artworks aw2 WHERE aw2.artist_id = aw.artist_id);` → возвращает 3-5 outliers. Кликни любую row.',
    },
  ],
  scoring: { baseScore: 9, firstAttemptBonus: 1, hintPenalty: 1, maxAttempts: 3 },
  achievementTriggers: [
    'detective-junior',
    'sprint-11-required',
    'sql-detective',
  ],
  estimateMin: 22,
  skills: { SQL: 5, 'Practice Lab': 5, 'Test Design': 1 },
  requires: ['sql-playground'],
};

// ─── B2-SQL-07 · S11 · Window function · running revenue per top artist ─────

const B2_SQL_07: Scenario = {
  id: 'B2-SQL-07',
  sprint: 11,
  tier: 'required',
  mode: 'investigate',
  rank: 'sql-detective-junior',
  startRoute: '/sql-playground/running-revenue',
  mountStrategy: 'iframe-route',
  customModule: 'block-2/sql-window-running-total.tsx',
  title: 'Window function · running total revenue',
  brief:
    'Серж: «Хочу видеть как накапливается revenue Леры день за днём за последние 30 дней. Для дня X показать revenue этого дня + cumulative с начала периода. Это window function задача».',
  taskMd: [
    '**Контекст.** Финансовая дашборда хочет график «накопленный revenue Леры за последний месяц». По дням: revenue за день + nakopittel\'ный с начала месяца.',
    '',
    'Серж: «Это классический случай window function `SUM() OVER (ORDER BY date)`. В обычном GROUP BY такое не сделать — group by сваривает rows, а нам нужно **per-row** значение с накопительной агрегацией».',
    '',
    '**Что делать.** Открой SQL Playground. Цель — query, которая возвращает по одной строке на день за последние 30 дней, с колонками:',
    '',
    '- `day` — дата',
    '- `daily_revenue` — revenue за день',
    '- `running_total` — накопленный revenue с начала окна',
    '',
    '1. Сначала агрегируй по дням: `SELECT date(o.created_at) AS day, SUM(o.total_rub) AS daily_revenue ...` для Леры за последние 30 дней, GROUP BY day.',
    '2. Заверни это в outer query или в CTE и применит window function: `SUM(daily_revenue) OVER (ORDER BY day) AS running_total`.',
    '3. Запусти query — получишь 30 rows (по одной на день), running_total монотонно растёт.',
    '4. Кликни на любую row в результате — это твой ответ.',
    '',
    '**Шаблон window function:**',
    '```sql',
    'SELECT col, AGG_FN(...) OVER (ORDER BY col) AS running',
    'FROM ... ;',
    '```',
    '',
    '**Что важно.** Window functions = «как GROUP BY, но без сворачивания rows». Старая школа делала это через self-JOIN или correlated subquery — медленно и непонятно. С window functions: **одна строка кода, чистая семантика, оптимизатор делает магию**. SUM/AVG/COUNT/MIN/MAX все имеют OVER-форму. Плюс есть позиционные: ROW_NUMBER · RANK · LAG · LEAD. Изучай эту группу — **это most-impactful вещь, которая отличает middle-SQL от senior-SQL**.',
  ].join('\n'),
  hints: [
    {
      text: 'Шаги: (1) GROUP BY date(created_at) для daily_revenue. (2) Заверни в WITH CTE: WITH daily AS (SELECT day, daily_revenue ...). (3) В outer query: SELECT day, daily_revenue, SUM(daily_revenue) OVER (ORDER BY day) AS running_total FROM daily.',
    },
    {
      text: '`OVER (ORDER BY day)` — это и есть window. Без OVER — просто SUM, всё сваривает. C OVER — running по упорядоченному окну.',
    },
    {
      text: 'Полный query (CTE-style): `WITH daily AS (SELECT date(o.created_at) AS day, SUM(o.total_rub) AS daily_revenue FROM orders o WHERE o.artist_id = (SELECT id FROM artists WHERE username=\'lera\') AND o.status=\'paid\' AND o.created_at >= datetime(\'now\', \'-30 days\') GROUP BY date(o.created_at)) SELECT day, daily_revenue, SUM(daily_revenue) OVER (ORDER BY day) AS running_total FROM daily ORDER BY day;` → 30 rows. Кликни любую.',
    },
  ],
  scoring: { baseScore: 10, firstAttemptBonus: 1, hintPenalty: 1, maxAttempts: 3 },
  achievementTriggers: [
    'detective-junior',
    'sprint-11-required',
    'sql-detective',
  ],
  estimateMin: 28,
  skills: { SQL: 6, 'Practice Lab': 5, 'Test Design': 1 },
  requires: ['sql-playground'],
};

// ─── B2-SQL-08 · S11 · ER diagram · find FK orphans ─────────────────────────

const B2_SQL_08: Scenario = {
  id: 'B2-SQL-08',
  sprint: 11,
  tier: 'required',
  mode: 'investigate',
  rank: 'sql-detective-junior',
  startRoute: '/sql-playground/er-orphans',
  mountStrategy: 'iframe-route',
  customModule: 'block-2/sql-er-diagram-orphans.tsx',
  title: 'ER diagram · найди orphan FK',
  brief:
    'В БД есть таблица comments с FK на users. Часть users была удалена hard-delete, но comments остались. Сколько orphan comments? Найди.',
  taskMd: [
    '**Контекст.** Эпизод из истории Atelier: 6 месяцев назад поддержка случайно сделала `DELETE FROM users WHERE ...` без CASCADE. Часть users исчезла, но их comments остались с broken FK (`comment.user_id` указывает на uers id, которого больше нет в users table).',
    '',
    'Серж: «Глянь ER diagram (открыта в side panel), найди FK relations, и посчитай сколько comments осиротело».',
    '',
    '**Что делать.** Открой SQL Playground. В side panel — **ER diagram viewer**: показывает таблицы, столбцы и FK-стрелки между ними.',
    '',
    '1. Изучи ER diagram. Найди FK relation `comments.user_id → users.id`.',
    '2. Напиши query, которая считает comments, у которых `user_id` указывает на несуществующий user.',
    '3. Hint: `LEFT JOIN comments LEFT JOIN users ... WHERE u.id IS NULL` (тот же паттерн что в B2-SQL-01).',
    '4. Запусти — получишь число (вероятно 50-200 orphan comments).',
    '5. Кликни на cell с числом в результате — это твой ответ.',
    '',
    '**Что важно.** ER diagram — это **визуализация связей таблиц**. Она помогает увидеть FK relationships без чтения каждого CREATE TABLE statement. Когда ты investigate чужую БД, всегда начинай с ER diagram — это даёт ментальную карту schema за 30 секунд. Orphan FK rows — это **violation referential integrity**: foreign key должен указывать на existing primary key, иначе нарушены contracts. В большинстве БД CASCADE/RESTRICT enforce это автоматически, но в legacy системах — приходится audit\'ить.',
  ].join('\n'),
  hints: [
    {
      text: 'ER diagram показывает FK как стрелку «comments.user_id ──→ users.id». Это значит каждый comment должен ссылаться на existing user.',
    },
    {
      text: 'LEFT JOIN сохраняет все comments, NULL подставляется для отсутствующих users. WHERE u.id IS NULL фильтрует только orphans.',
    },
    {
      text: 'Query: `SELECT COUNT(*) FROM comments c LEFT JOIN users u ON u.id = c.user_id WHERE u.id IS NULL;` → возвращает число orphan comments. Кликни на cell с числом.',
    },
  ],
  scoring: { baseScore: 8, firstAttemptBonus: 1, hintPenalty: 1, maxAttempts: 3 },
  achievementTriggers: [
    'detective-junior',
    'sprint-11-required',
    'sql-detective',
  ],
  estimateMin: 20,
  skills: { SQL: 4, 'Practice Lab': 5, 'Documentation': 2 },
  requires: ['sql-playground'],
};

// ─── B2-SQL-09 · S11 · NoSQL toy · count discrepancy across models ──────────

const B2_SQL_09: Scenario = {
  id: 'B2-SQL-09',
  sprint: 11,
  tier: 'bonus',
  mode: 'investigate',
  rank: 'sql-detective-junior',
  startRoute: '/sql-playground/relational-vs-nosql',
  mountStrategy: 'iframe-route',
  customModule: 'block-2/sql-nosql-comparison.tsx',
  title: 'NoSQL toy · разный count в двух моделях',
  brief:
    'NoSQL view показывает 50 ratings, relational SQL — 47. Где 3? Подсказка: разная семантика «отсутствия».',
  taskMd: [
    '**Контекст.** Atelier пилотит миграцию ratings из relational table в NoSQL collection. Серж положил рядом обе виды: relational SQL playground (table `ratings`) + NoSQL toy view (collection `ratings_nosql`, read-only).',
    '',
    'Менеджер: «В NoSQL вижу 50 docs. В SQL — 47 rows. Это одни и те же данные? Как выкрутиться 3?»',
    '',
    '**Что делать.** Открой Playground. Слева relational SQL editor. Справа NoSQL collection viewer (показывает documents в JSON-стиле).',
    '',
    '1. Запусти `SELECT COUNT(*) FROM ratings;` → 47.',
    '2. В NoSQL view переключись на «Document count» — увидишь 50 docs.',
    '3. Поскролль NoSQL docs. Найди те 3 docs, которые отличаются от обычных.',
    '4. Что у этих 3 docs внутри? Какое поле/значение делает их «специальными»?',
    '5. Сравни с relational table — почему этих 3 нет в `ratings` table?',
    '6. Найди один из 3 docs в NoSQL view, кликни на него — это твой ответ.',
    '',
    '**Что важно.** Document databases (NoSQL) и relational tables представляют **отсутствующие данные** по-разному:',
    '',
    '- **Relational:** если rating «удалена» — row физически удалена (DELETE), её нет в COUNT.',
    '- **NoSQL document:** часто rating сохраняется как `{ id: ..., rating: null, deleted_at: ... }` — document остаётся, поле `rating` = null. COUNT documents учитывает их.',
    '',
    'Это **не баг** — это **разная семантика** между моделями данных. Когда мигрируешь между ними, нужно явно решать: tombstone delete (NoSQL) → physical delete (relational)? Или наоборот сохранять «soft-deleted» rows? Эти решения принимаются ДО миграции, а не во время.',
  ].join('\n'),
  hints: [
    {
      text: 'Поскролль NoSQL docs внимательно. У 3 docs значение `rating` отличается от остальных — null или deleted флаг.',
    },
    {
      text: 'NoSQL хранит «deleted» как documents с `rating: null` или `deleted_at: <timestamp>`. Relational при DELETE удаляет row физически.',
    },
    {
      text: '3 «лишние» docs в NoSQL имеют `rating: null` (юзер удалил свой rating, но document остался как audit trail). В relational эти rows были удалены через DELETE — отсюда 47, не 50. Кликни на один из null-docs.',
    },
  ],
  scoring: { baseScore: 9, firstAttemptBonus: 1, hintPenalty: 1, maxAttempts: 3 },
  achievementTriggers: [
    'detective-junior',
    'sprint-11-bonus',
    'sql-detective',
    'curious-cat',
  ],
  estimateMin: 25,
  skills: { SQL: 4, 'Practice Lab': 5, 'Test Design': 2, 'Requirements Analysis': 1 },
  requires: ['sql-playground'],
};

// ─── B2-SQL-10 · S11 · Author · Faker test data spec ────────────────────────

const B2_SQL_10: Scenario = {
  id: 'B2-SQL-10',
  sprint: 11,
  tier: 'required',
  mode: 'author',
  rank: 'author-apprentice',
  startRoute: '/sql-playground/test-data-spec',
  mountStrategy: 'iframe-route',
  title: 'Тестовые данные · spec для 1000 artworks',
  brief:
    'Серж нужно нагрузочное тестирование search-endpoint. Напиши Faker-style spec для генерации 1000 realistic artworks с edge cases.',
  taskMd: [
    '**Контекст.** Серж: «Будем нагрузочно тестировать `GET /api/search`. Нужно 1000 artworks в БД, реалистичных по distributions. Напиши spec, который мы передадим Faker-генератору».',
    '',
    '**Что делать.** В textarea ниже напиши spec в JSON-формате (или structured-таблице — на твой вкус). Spec должен описывать:',
    '',
    '**1. Schema · поля artworks с типами и правилами генерации:**',
    '- `id` (UUID)',
    '- `title` (текст 2-5 слов · можно Lorem)',
    '- `artistId` (FK на artists · weighted distribution: популярных артистов больше)',
    '- `priceRub` (number · range 500-100000 · log-normal distribution mean ~5000)',
    '- `tags` (массив из 1-5 tags · из ~50 предзаданных)',
    '- `createdAt` (datetime · from 2024-01-01 to now · linear distribution)',
    '- `status` (enum draft/review/published · weights 0.05/0.10/0.85)',
    '',
    '**2. Edge cases · конкретные включения для тестирования edge:**',
    '- N artworks с unicode emoji в title (для кодировки)',
    '- N artworks с extreme price (>50000 ₽ · для price-range filter)',
    '- N artworks с тэгом «single-occurrence» (для search relevance test)',
    '- N artworks с status=draft (для visibility/permission test)',
    '',
    '**3. Total count и distribution rationale:**',
    'Объясни 1-2 предложениями, почему именно такие weights/distributions реалистичны.',
    '',
    '**Что важно.** Test data spec — это **бизнес-документ**, а не код. Когда Серж скажет «сделай 1000 artworks», без spec ты получишь «1000 рандомных строк» — нерелевантных для нагрузочного теста. Хороший spec учитывает (а) реалистичные distributions (длинный хвост по price вместо uniform), (б) edge cases (unicode · extreme values · rare combos), (в) бизнес-правила (status=published преобладает). Faker-style spec — переиспользуемый artifact: положил в репо, генерируй заново при изменении схемы.',
  ].join('\n'),
  rubric: {
    items: [
      {
        id: 'R1',
        text: 'Spec в structured формате (JSON или таблица) · не свободный текст',
      },
      {
        id: 'R2',
        text: 'Все 7 obligatory полей описаны (id, title, artistId, priceRub, tags, createdAt, status)',
      },
      {
        id: 'R3',
        text: 'Для каждого numeric/date поля указан range (min/max/from/to)',
      },
      {
        id: 'R4',
        text: 'Distributions явно названы (random / weighted / log-normal / linear · хотя бы для 2-3 полей)',
      },
      {
        id: 'R5',
        text: 'Edge cases: минимум 3 явных кейса с конкретными числами (не «несколько» · «немного»)',
      },
      {
        id: 'R6',
        text: 'Edge cases покрывают разные measurement axis (charset / boundaries / distribution corners)',
      },
      {
        id: 'R7',
        text: 'Status weights указаны явно (например 0.05/0.10/0.85 = draft/review/published)',
      },
      {
        id: 'R8',
        text: 'Bonus: rationale объясняет, почему такие distributions реалистичны',
      },
    ],
    passThreshold: 6,
  },
  sampleAnswer: [
    '```json',
    '{',
    '  "count": 1000,',
    '  "schema": {',
    '    "id": { "type": "uuid" },',
    '    "title": { "type": "lorem", "words": "2-5" },',
    '    "artistId": {',
    '      "type": "fk",',
    '      "table": "artists",',
    '      "distribution": "weighted",',
    '      "rationale": "Топ-10% artists получают 70% работ (Pareto)"',
    '    },',
    '    "priceRub": {',
    '      "type": "number",',
    '      "min": 500,',
    '      "max": 100000,',
    '      "distribution": "log-normal",',
    '      "mean": 5000',
    '    },',
    '    "tags": {',
    '      "type": "array",',
    '      "values": "[abstract, portrait, landscape, ... 50 предзаданных]",',
    '      "minItems": 1,',
    '      "maxItems": 5',
    '    },',
    '    "createdAt": {',
    '      "type": "datetime",',
    '      "from": "2024-01-01",',
    '      "to": "now",',
    '      "distribution": "linear"',
    '    },',
    '    "status": {',
    '      "type": "enum",',
    '      "values": ["draft", "review", "published"],',
    '      "weights": [0.05, 0.10, 0.85]',
    '    }',
    '  },',
    '  "edgeCases": [',
    '    "10 artworks с unicode emoji в title (например \\"Закат 🌅 в Крыму\\")",',
    '    "5 artworks с extreme price >50000 ₽ (для price-range filter)",',
    '    "3 artworks с тэгом \\"rare-artist\\" единственным вхождением (search relevance)",',
    '    "20 artworks status=draft без artistId (для draft visibility test)"',
    '  ],',
    '  "rationale": "Log-normal по price моделирует реальный art market: преобладают доступные работы (~5000 ₽), хвост дорогих. Pareto на artistId — реалистичная кривая популярности. Status weights отражают что 85% работ — published (drafts/reviews короткое жизненное окно)."',
    '}',
    '```',
  ].join('\n'),
  hints: [
    {
      text: 'Faker spec ≠ код. Это **описание правил** генерации. Хороший формат — JSON с полями: schema (типы + правила), edgeCases (конкретные включения), rationale (почему такие правила).',
    },
    {
      text: 'Distributions: «random» — uniform, «weighted» — некоторые значения чаще других, «log-normal» — хвост (типично для money/sizes), «linear» — равномерный рост во времени. Выбирай по бизнес-смыслу.',
    },
    {
      text: 'Edge cases — это **вручную заданные включения**, которые рандом может никогда не сгенерировать. «10 artworks с emoji в title» — рандом не выдаст (low probability). Прописываешь явно для гарантии coverage.',
    },
  ],
  scoring: { baseScore: 10, firstAttemptBonus: 1, hintPenalty: 1, maxAttempts: 3 },
  achievementTriggers: [
    'author-junior',
    'sprint-11-required',
    'sql-detective',
    'test-design',
  ],
  estimateMin: 25,
  skills: { SQL: 3, 'Practice Lab': 5, 'Test Design': 4, 'Documentation': 2 },
  requires: ['sql-playground'],
};

// ────────────────────────────────────────────────────────────────────────────
// Sprint 07 · Workspace tools — B2-WEB-01..05
// Pattern: Author-heavy (4 of 5) — Jira/TestRail/Confluence-like artifacts.
// One Investigate (B2-WEB-04 broken-workflow) через customModule.
// Skills: Workspace Tools, Documentation, QA Concepts.
// ────────────────────────────────────────────────────────────────────────────

// ─── B2-WEB-01 · S07 · Author · Jira ticket from bug report ─────────────────

const B2_WEB_01: Scenario = {
  id: 'B2-WEB-01',
  sprint: 7,
  tier: 'required',
  mode: 'author',
  rank: 'author-apprentice',
  startRoute: '/admin/jira-shell/new-ticket',
  mountStrategy: 'iframe-route',
  title: 'Перенеси bug-report в Jira ticket',
  brief:
    'В Confluence лежит rough bug-report. Серж: «Перенеси в Jira правильно — со steps, expected/actual, severity, priority, environment».',
  taskMd: [
    '**Контекст.** Аня (тестировщица) написала в Confluence свободный текст про обнаруженный баг:',
    '',
    '> «На странице корзины если ввести промокод, который начинается с пробела (например " STUDIO10"), система принимает его как валидный, но скидка не применяется. Я проверила в Chrome 127 на Win11. Ожидала либо trim пробела + применение, либо сообщение об ошибке. Происходит ни то ни другое — просто принимает и молчит. Это в проде».',
    '',
    'Серж: «Текст ок, но в Jira нужна структура. Перенеси в правильный формат — мы будем работать по нему».',
    '',
    '**Что делать.** Открой Jira-shell (`/admin/jira-shell/new-ticket`). Заполни поля нового ticket из информации в Confluence:',
    '',
    '- **Title**',
    '- **Type** (Bug / Task / Story / Epic)',
    '- **Severity** (Critical / High / Medium / Low)',
    '- **Priority** (P0-Blocker / P1-High / P2-Medium / P3-Low)',
    '- **Environment** (Browser / OS / Build version)',
    '- **Description** (что произошло · 1-2 предложения)',
    '- **Steps to reproduce** (numbered list)',
    '- **Expected result**',
    '- **Actual result**',
    '- **Attachments** (если applicable · screenshot / video)',
    '',
    '**Что важно.** Хороший bug-report = воспроизводимый и actionable. Структура нужна не для бюрократии — она заставляет автора собрать все факты до того как разработчик начнёт читать. Без steps to reproduce разработчик гадает. Без environment — не понимает, специфика браузера или нет. Без severity — менеджер не знает приоритет. Тренируйся писать структурно даже если внутренний голос «и так всё понятно».',
  ].join('\n'),
  rubric: {
    items: [
      {
        id: 'R1',
        text: 'Title описательный (не «Bug в корзине», а «Промокод с leading space принимается без trim, но скидка не применяется» или подобное)',
      },
      { id: 'R2', text: 'Type = Bug (не Task / Story)' },
      { id: 'R3', text: 'Severity указана с обоснованием (Medium reasonable: бизнес-влияние есть, но workaround есть)' },
      { id: 'R4', text: 'Priority указана (P2-Medium reasonable)' },
      {
        id: 'R5',
        text: 'Environment: явно указаны browser (Chrome 127), OS (Win11), build/env (production)',
      },
      {
        id: 'R6',
        text: 'Steps to reproduce — numbered list, конкретные действия (открой / введи / нажми)',
      },
      {
        id: 'R7',
        text: 'Expected vs Actual явно разделены и сформулированы',
      },
      { id: 'R8', text: 'Bonus: упомянуто что failure mode silent (нет ошибки на UI)' },
    ],
    passThreshold: 6,
  },
  sampleAnswer: [
    '**Title:** «Промокод с leading space принимается без trim, но скидка не применяется (silent failure)»',
    '',
    '**Type:** Bug',
    '**Severity:** Medium (workaround есть · ввести без пробела · но silent failure скрывает root cause от пользователя)',
    '**Priority:** P2-Medium',
    '**Environment:** Chrome 127 / Windows 11 / production',
    '',
    '**Description:** При вводе валидного промокода с leading space система принимает его как «валидный», но скидку не применяет. Failure silent — пользователь не понимает, почему промо не сработало.',
    '',
    '**Steps to reproduce:**',
    '1. Открыть `/cart` с одним валидным товаром в корзине.',
    '2. В поле «промокод» ввести `" STUDIO10"` (с пробелом в начале).',
    '3. Нажать «Применить».',
    '4. Посмотреть на блок промо/итог.',
    '',
    '**Expected result:**',
    'Один из двух acceptable исходов:',
    '(a) Система делает trim leading whitespace, применяет промо успешно (-10% к total).',
    '(b) Система не делает trim, показывает ошибку «Промокод не найден» (или подобную).',
    '',
    '**Actual result:**',
    'Поле принимает ввод, кнопка «Применить» меняет визуально на «Применено», но total не меняется. Никакой ошибки на UI. Console clean.',
    '',
    '**Attachments:**',
    '- Screenshot с введённым `" STUDIO10"` и неизменным total',
    '- HAR-файл network tab (опционально)',
  ].join('\n'),
  hints: [
    {
      text: 'Title — это то, что devs увидят в backlog grid. «Bug в корзине» неинформативно. «Промокод с leading space принимается, но скидка не применяется» — конкретно.',
    },
    {
      text: 'Severity ≠ Priority. Severity — насколько баг плох технически (data loss vs cosmetic). Priority — насколько срочно фиксить (бизнес-импакт, сколько юзеров затронуто). Можно: высокая severity + низкий priority (rare edge case, но technically серьёзный).',
    },
    {
      text: 'Steps to reproduce — самый важный пункт. Напиши их так, чтобы dev мог воспроизвести **без вопросов**: open URL → enter exact text → click exact button → observe specific outcome.',
    },
  ],
  scoring: { baseScore: 8, firstAttemptBonus: 0, hintPenalty: 1, maxAttempts: 3 },
  achievementTriggers: [
    'author-junior',
    'sprint-07-required',
    'workspace-tools',
    'documentation',
  ],
  estimateMin: 18,
  skills: {
    'Workspace Tools': 4,
    'Practice Lab': 5,
    Documentation: 4,
    'QA Concepts': 1,
  },
  requires: ['msw-api'],
};

// ─── B2-WEB-02 · S07 · Author · TestRail run config ─────────────────────────

const B2_WEB_02: Scenario = {
  id: 'B2-WEB-02',
  sprint: 7,
  tier: 'required',
  mode: 'author',
  rank: 'author-apprentice',
  startRoute: '/admin/testrail-shell/new-run',
  mountStrategy: 'iframe-route',
  title: 'TestRail test run для auth-flow regression',
  brief:
    'Sprint 9 release · auth flow updates. Серж: «Создай test run в TestRail-like UI: правильные test cases, assignees, milestone, due date».',
  taskMd: [
    '**Контекст.** Sprint 9 на этой неделе релизит updates auth flow (login + signup + password-reset). Перед release — нужен полный regression smoke pass. Серж: «Создай test run в TestRail. У нас уже есть test catalog `Auth Flow` с 12 cases. Назначь нужных людей, привяжи к milestone, поставь due date».',
    '',
    '**Что делать.** Открой TestRail-shell (`/admin/testrail-shell/new-run`). Заполни конфигурацию test run:',
    '',
    '- **Run name** (descriptive · указывает, что/когда/зачем)',
    '- **Project** (выбери: Atelier)',
    '- **Milestone** (выбери: «Sprint 9 release · auth-flow updates»)',
    '- **Suite/Section** (выбери: «Auth Flow» · 12 cases)',
    '- **Test cases** (выбери конкретные cases из 12 · все или подмножество с обоснованием)',
    '- **Assignees** (распредели cases между QA team: Аня · Лера · Серж)',
    '- **Due date** (релиз через 3 дня · регресс должен закрыться за 2 дня = day before release)',
    '- **Description** (1-2 предложения · что в release, на что обратить особое внимание)',
    '',
    '**Что важно.** Test run в TestRail — это не «список тестов». Это **обязательство команды** за состояние feature перед release. Когда run настроен правильно: (а) каждый case имеет owner, (б) дата фиксирует accountability, (в) milestone tracking показывает coverage release. Без правильной настройки регресс размазывается, ownership теряется, due date проскакивает.',
  ].join('\n'),
  rubric: {
    items: [
      {
        id: 'R1',
        text: 'Run name описательный (включает feature + sprint + тип run · напр. «Auth Flow regression · Sprint 9 release»)',
      },
      {
        id: 'R2',
        text: 'Milestone привязан к Sprint 9 release · auth-flow updates',
      },
      {
        id: 'R3',
        text: 'Selected test cases: либо все 12, либо подмножество с явным rationale (что включено, что нет)',
      },
      {
        id: 'R4',
        text: 'Assignees: ≥2 человека (Серж + хотя бы одна из Ани/Леры) · с распределением cases',
      },
      {
        id: 'R5',
        text: 'Due date указан, до даты релиза (релиз через 3 дня → due date day before · 2 дня от now)',
      },
      {
        id: 'R6',
        text: 'Description упоминает что в release (auth flow updates) и focus areas',
      },
      {
        id: 'R7',
        text: 'Bonus: указан priority subset cases (для smoke vs full regression split)',
      },
    ],
    passThreshold: 5,
  },
  sampleAnswer: [
    '**Run name:** «Auth Flow regression · Sprint 9 release · pre-deploy»',
    '',
    '**Project:** Atelier',
    '',
    '**Milestone:** Sprint 9 release · auth-flow updates',
    '',
    '**Suite/Section:** Auth Flow (12 cases)',
    '',
    '**Test cases:** Все 12 cases выбраны. Smoke subset (P0): 4 cases (TC-A01 login happy, TC-A03 signup happy, TC-A07 password-reset happy, TC-A11 session expiry). Остальные 8 — full regression.',
    '',
    '**Assignees:**',
    '- **Серж** — TC-A01..A04 (login + happy paths · 4 cases)',
    '- **Аня** — TC-A05..A08 (signup + edge cases · 4 cases)',
    '- **Лера** — TC-A09..A12 (password-reset + session · 4 cases)',
    '',
    '**Due date:** day before release (через 2 дня · 2026-05-06)',
    '',
    '**Description:**',
    'Sprint 9 релизит updates auth flow: новый password-reset flow + improved error messages + token expiry rotation. Focus areas: (а) password-reset email path с прежней работой; (б) token rotation не ломает существующие активные сессии; (в) error messages консистентны между login + signup.',
    '',
    '**Smoke subset (P0)** — должен пройти к концу day 1: 4 cases (TC-A01 / TC-A03 / TC-A07 / TC-A11). Если smoke падает — release blocked.',
  ].join('\n'),
  hints: [
    {
      text: 'Run name = что/когда/зачем. «Auth regression Sprint 9» — этого хватит, чтобы потом найти run в истории.',
    },
    {
      text: 'Assignees распределяй так, чтобы один человек не остался с 12 cases (нагрузка), а каждый отвечал за coherent slice (например — login отдельно, signup отдельно, reset отдельно). 3 человека × 4 cases ровно вписывается.',
    },
    {
      text: 'Due date считается ОТ даты релиза, не от now. Релиз через 3 дня → day before release = day 2 (даём buffer на failures). Если поставишь due date == release day, regression может не успеть закрыться.',
    },
  ],
  scoring: { baseScore: 8, firstAttemptBonus: 0, hintPenalty: 1, maxAttempts: 3 },
  achievementTriggers: [
    'author-junior',
    'sprint-07-required',
    'workspace-tools',
  ],
  estimateMin: 22,
  skills: {
    'Workspace Tools': 5,
    'Practice Lab': 5,
    'Test Design': 2,
    Documentation: 2,
  },
  requires: ['msw-api'],
};

// ─── B2-WEB-03 · S07 · Author · Confluence bug triage matrix ────────────────

const B2_WEB_03: Scenario = {
  id: 'B2-WEB-03',
  sprint: 7,
  tier: 'required',
  mode: 'author',
  rank: 'author-apprentice',
  startRoute: '/admin/confluence-shell/new-page',
  mountStrategy: 'iframe-route',
  title: 'Bug triage matrix в Confluence',
  brief:
    'Менеджер: «За last sprint у нас 47 open bugs. Нужна triage matrix — severity × priority, owner на каждый critical, ETA. Confluence».',
  taskMd: [
    '**Контекст.** Менеджер: «У нас raw bug list, ~47 open bugs. Без triage команда уйдёт по случайному порядку. Серж попросил тебя сделать triage matrix — структурированную страницу в Confluence, чтобы спринт-обзор шёл по ней».',
    '',
    '**Bug list (excerpt из Jira):**',
    '',
    'Из 47 bugs distribution:',
    '- 3 Critical / High severity (data loss · security · checkout broken)',
    '- 8 Medium severity (промокод issue · sort bug · UI mismatch)',
    '- 22 Low severity (cosmetic · minor copy · padding)',
    '- 14 Edge case / non-reproducible (вернётся в backlog или close as «not a bug»)',
    '',
    '**Что делать.** Открой Confluence-shell (`/admin/confluence-shell/new-page`). Создай triage page со следующей структурой:',
    '',
    '**1. Title + краткое summary (1-2 предложения):**',
    'Что в этом triage документе, на какой sprint relevant.',
    '',
    '**2. Severity × Priority matrix (table 4×4 или адаптированная):**',
    'Заголовки: severity на колонках, priority на rows. В каждой клетке — bug ID или count. Critical bugs: bug ID + owner + ETA в самой клетке.',
    '',
    '**3. Action items секция:**',
    '- Который Critical нужно фиксить first (1-2 пункта).',
    '- Какие Medium можно перенести в next sprint (3-5 пунктов).',
    '- Какие Low can wait until release+1 (общий пункт).',
    '',
    '**4. Decisions log:**',
    'Кто принял какое решение по triage (Серж + менеджер).',
    '',
    '**Что важно.** Confluence triage page = **shared truth** для команды. Без неё каждый запоминает приоритеты по-своему — конфликт. С ней: все смотрят на одну таблицу, спорят над конкретными ячейками. Severity × Priority matrix — стандартная форма, потому что severity (technical) ≠ priority (business). Bug может быть high-severity / low-priority (rare edge case, security risk но никто не использует), или low-severity / high-priority (cosmetic, но на главном экране).',
  ].join('\n'),
  rubric: {
    items: [
      {
        id: 'R1',
        text: 'Title описательный (включает sprint + тип документа)',
      },
      {
        id: 'R2',
        text: 'Severity × Priority matrix присутствует, оформлена таблицей',
      },
      {
        id: 'R3',
        text: 'Critical bugs (3) расписаны явно: bug ID + owner + ETA в каждой ячейке',
      },
      {
        id: 'R4',
        text: 'Medium / Low distribution показаны (count или summary)',
      },
      {
        id: 'R5',
        text: 'Action items секция: «что фиксить first», «что переносим», «что can wait»',
      },
      {
        id: 'R6',
        text: 'Decisions log упоминает кто принял решения (хотя бы 2 stakeholder)',
      },
      {
        id: 'R7',
        text: 'Bonus: указано когда matrix обновляется (frequency · ownership)',
      },
    ],
    passThreshold: 5,
  },
  sampleAnswer: [
    '**Title:** «Sprint 9 · Bug Triage Matrix · 47 open bugs»',
    '',
    '**Summary:** Triage 47 open bugs, накопленных за прошлый спринт. Решение по приоритезации перед планированием Sprint 10. Документ обновляется ежедневно до старта Sprint 10.',
    '',
    '**Severity × Priority matrix:**',
    '',
    '| | P0 (now) | P1 (this sprint) | P2 (next sprint) | P3 (backlog) |',
    '|---|---|---|---|---|',
    '| Critical | BUG-447 — checkout broken — Серж — ETA 2026-05-05 | BUG-451 — data loss on cart refresh — Аня — ETA 2026-05-06 | — | — |',
    '| High | — | BUG-462 — auth token leak — Лера — ETA 2026-05-07 | — | — |',
    '| Medium | — | 2 bugs (promo + sort) | 6 bugs | — |',
    '| Low | — | — | 4 bugs | 18 bugs |',
    '| Edge / non-repro | — | — | — | 14 bugs (review · close) |',
    '',
    '**Action items:**',
    '- **P0 фиксить first:** BUG-447 (checkout broken · блокирует release v1.0). Серж · ETA 2026-05-05 (через 1 день).',
    '- **Medium → next sprint:** 6 medium bugs не критичны для release v1.0, переносим в Sprint 10. 2 medium — критичны, остаются P1.',
    '- **Low can wait:** 18 low-severity / low-priority — review в Sprint 11 после release. Не блокируем.',
    '- **Edge / non-repro:** 14 bugs — Аня closes как «cannot reproduce» или «not a bug» с rationale в каждом.',
    '',
    '**Decisions log:**',
    '- 2026-05-04: Серж + менеджер. Принято: P0 = только 1 bug (BUG-447). Остальные critical = P1, фиксим параллельно. Rationale: команда из 3 человек, P0-параллельность не работает.',
    '- 2026-05-04: Серж. Принято: 18 low-severity отложены в backlog. Rationale: cosmetic, не влияет на release narrative.',
    '',
    '**Update frequency:** ежедневно (Серж) до start of Sprint 10. После — в backlog manager.',
  ].join('\n'),
  hints: [
    {
      text: 'Severity × Priority matrix = **2 axis**. Severity = technical impact (data loss / security / cosmetic). Priority = business urgency (now / this sprint / next / backlog). Bug может быть high-severity / low-priority (security risk на rare path).',
    },
    {
      text: 'Critical bugs пиши в ячейку с детализацией: bug ID + owner + ETA. Medium/Low — count или summary (нет смысла раздувать таблицу 47 bugs строк).',
    },
    {
      text: 'Decisions log = **кто решил, когда, почему**. Через 2 недели в Sprint 10 retro когда менеджер спросит «почему мы это перенесли», ответ должен быть в документе, а не в чьей-то памяти.',
    },
  ],
  scoring: { baseScore: 9, firstAttemptBonus: 0, hintPenalty: 1, maxAttempts: 3 },
  achievementTriggers: [
    'author-junior',
    'sprint-07-required',
    'workspace-tools',
    'documentation',
  ],
  estimateMin: 25,
  skills: {
    'Workspace Tools': 4,
    'Practice Lab': 5,
    Documentation: 5,
    'Test Design': 2,
  },
  requires: ['msw-api'],
};

// ─── B2-WEB-04 · S07 · Investigate · broken Jira workflow auto-reopen ───────

const B2_WEB_04: Scenario = {
  id: 'B2-WEB-04',
  sprint: 7,
  tier: 'required',
  mode: 'investigate',
  rank: 'detective-junior',
  startRoute: '/admin/jira-shell/workflows',
  mountStrategy: 'iframe-route',
  customModule: 'block-2/jira-broken-workflow.tsx',
  title: 'Сломанный Jira workflow · auto-reopen',
  brief:
    'Resolved bugs сами reopen через 5 минут. Серж: «Где-то в workflow rules ошибка. Глянь конфиг workflow `Bug` — там правило, которое неправильно настроено».',
  taskMd: [
    '**Контекст.** За последнюю неделю команда заметила: каждый bug, переведённый в `Resolved`, через 5 минут автоматически возвращается в `Open`. QA злится: «Я только закрыл ticket — он опять у меня в очереди!»',
    '',
    'Серж: «Это auto-rule в workflow. У нас есть правило про auto-reopen if no QA verification — оно должно срабатывать через 24 часа после resolve, а не через 5 минут. Найди правило, посмотри его конфиг, кликни на сломанное поле/значение».',
    '',
    '**Что делать.** Открой Jira-shell workflows (`/admin/jira-shell/workflows`). Найди workflow `Bug`. Просмотри список Auto-rules:',
    '',
    '- Rule 1: Auto-assign to component owner on creation',
    '- Rule 2: Notify reporter on status change',
    '- Rule 3: **Auto-reopen if no QA verification** — это твой target',
    '- Rule 4: Auto-close after 30 days no activity',
    '',
    '1. Открой Rule 3 «Auto-reopen if no QA verification».',
    '2. Посмотри её конфиг: trigger / condition / delay / action.',
    '3. Сравни с описанием в Confluence (side panel): «через 24 часа»',
    '4. Найди поле в конфиге, в котором значение нарушает intent (5 minutes vs 24 hours).',
    '5. Кликни на это поле в UI workflow editor — это твой ответ.',
    '',
    '**Что важно.** Workflow auto-rules в Jira — это **дешёвая автоматизация бизнес-процессов**, но также **источник тонких багов**. Пример: time-based rule с неправильным units (minutes vs hours), trigger которое срабатывает каждый раз вместо conditionally. Когда расследуешь странное поведение — workflow auto-rules первое место смотреть. Принцип: **rule никогда не должен противоречить тому, что заявлено в его имени**. Если имя rule «через 24 часа», но конфиг = 5 минут — это либо переименование, либо bug.',
  ].join('\n'),
  hints: [
    {
      text: 'Открой Rule 3 «Auto-reopen if no QA verification». Посмотри поле «Delay» — это и есть time-параметр. Какое значение там стоит?',
    },
    {
      text: 'Field «Delay» имеет значение `5` и unit `minutes`. По описанию в Confluence должно быть `24 hours` (или эквивалент в minutes = 1440).',
    },
    {
      text: 'Поле `Delay value: 5 minutes` — нарушение intent. Должно быть `1440 minutes` (24 hours) или units сменить на `hours: 24`. Кликни на cell с цифрой `5` в Delay поле.',
    },
  ],
  scoring: { baseScore: 8, firstAttemptBonus: 1, hintPenalty: 1, maxAttempts: 3 },
  achievementTriggers: [
    'detective-junior',
    'sprint-07-required',
    'workspace-tools',
  ],
  estimateMin: 22,
  skills: {
    'Workspace Tools': 4,
    'Practice Lab': 5,
    'Test Design': 1,
    'Requirements Analysis': 1,
  },
  requires: ['msw-api'],
};

// ─── B2-WEB-05 · S07 · Author · sprint planning agenda ──────────────────────

const B2_WEB_05: Scenario = {
  id: 'B2-WEB-05',
  sprint: 7,
  tier: 'bonus',
  mode: 'author',
  rank: 'author-apprentice',
  startRoute: '/admin/confluence-shell/new-page',
  mountStrategy: 'iframe-route',
  title: 'Sprint planning agenda',
  brief:
    'Завтра planning. Серж: «Подготовь agenda — кто рассказывает что, время, артефакты на выходе. Confluence».',
  taskMd: [
    '**Контекст.** Завтра 9:30 — Sprint 10 planning meeting. Серж попросил тебя подготовить agenda.',
    '',
    '**Команда:** Аня (QA) · Лера (QA) · Серж (lead) · Денис (dev) · Илья (dev) · Маша (PM).',
    '',
    '**Цель meeting:** определить scope Sprint 10. Выходные артефакты: sprint backlog (jira-tickets) + sprint goal (1 предложение) + capacity баланс по людям.',
    '',
    '**Что делать.** Открой Confluence-shell. Создай страницу agenda со структурой:',
    '',
    '- **Title** (descriptive)',
    '- **Datetime / location** (9:30 · video call)',
    '- **Participants** (явно назвать)',
    '- **Pre-read** (что прочитать до meeting · ссылки)',
    '- **Agenda items** (с временем на каждый):',
    '  - Sprint 9 retrospective summary',
    '  - Backlog grooming · candidate tickets review',
    '  - Capacity check (PTO · holidays · в командировке)',
    '  - Sprint goal formulation',
    '  - Final scope commit',
    '- **Output artifacts** (что появится в результате)',
    '- **Decisions to be made** (в каком вопросе нужен явный consensus)',
    '',
    '**Что важно.** Agenda без time-budget превращает 1-час meeting в 2-часовой. Каждый punkt должен иметь outline времени (5-10-15 минут). Pre-read важен: без него meeting начинается с «давайте все прочитаем backlog». Output artifacts важны: без них meeting кончается «спасибо, до завтра». Decision points важны: meeting без явных решений = waste of time.',
  ].join('\n'),
  rubric: {
    items: [
      { id: 'R1', text: 'Title указывает meeting type + sprint номер' },
      { id: 'R2', text: 'Datetime / location явно указаны' },
      { id: 'R3', text: 'Participants все 6 человек перечислены' },
      { id: 'R4', text: 'Pre-read указан (≥1 ссылка / артефакт для чтения до meeting)' },
      {
        id: 'R5',
        text: 'Agenda items 4-6 пунктов с временным budget на каждый (5-15 мин)',
      },
      {
        id: 'R6',
        text: 'Output artifacts перечислены (≥3 · sprint backlog · sprint goal · capacity баланс)',
      },
      {
        id: 'R7',
        text: 'Decisions to be made секция явно фиксирует «что должно быть decide\'нуто»',
      },
      {
        id: 'R8',
        text: 'Bonus: указан facilitator (кто ведёт meeting · обычно Серж lead) и note-taker',
      },
    ],
    passThreshold: 5,
  },
  sampleAnswer: [
    '**Title:** «Sprint 10 Planning · 2026-05-05 9:30»',
    '',
    '**Datetime / location:** 2026-05-05 · 9:30 — 11:00 (1.5h) · Zoom link [TBD]',
    '',
    '**Participants:**',
    '- Серж (QA lead · facilitator)',
    '- Аня (QA)',
    '- Лера (QA)',
    '- Денис (dev)',
    '- Илья (dev)',
    '- Маша (PM · note-taker)',
    '',
    '**Pre-read:**',
    '- Sprint 9 retrospective summary [link to retro page]',
    '- Sprint 10 candidate backlog [link to Jira filter]',
    '- Sprint 9 release notes (что выкатили)',
    '',
    '**Agenda:**',
    '',
    '| Time | Item | Owner |',
    '|---|---|---|',
    '| 5 мин | Sprint 9 retrospective recap (3 ключевых insight) | Серж |',
    '| 15 мин | Backlog grooming · просмотр 12 candidate tickets, оценка | All |',
    '| 5 мин | Capacity check · PTO / holidays / распределение времени | Маша |',
    '| 10 мин | Sprint goal formulation · одно предложение | Серж + Маша |',
    '| 30 мин | Detailed scope · ticket-by-ticket commit · estimate | All |',
    '| 5 мин | Final scope review · sign-off | All |',
    '| 10 мин | Buffer / Q&A / next steps | All |',
    '',
    '**Output artifacts:**',
    '- Sprint 10 backlog в Jira (commit-tickets с estimates)',
    '- Sprint 10 goal (1 предложение в Confluence sprint page)',
    '- Capacity баланс table (часы / человек)',
    '',
    '**Decisions to be made:**',
    '- Какой именно scope Sprint 10 (sliders в backlog)',
    '- Как распределяем capacity между QA bug-fix vs new feature work',
    '- Кто owner для each commit-ticket',
    '',
    '**Facilitator:** Серж',
    '**Note-taker:** Маша',
  ].join('\n'),
  hints: [
    {
      text: 'Время-budget per item — это не косметика. Если суммарно > 90 минут, meeting overrun. Если < 60 минут — риск что забыли обсудить важное.',
    },
    {
      text: 'Pre-read = «что должно быть прочитано до meeting». Без него meeting тратит первые 15 минут на чтение. Хороший Pre-read содержит ≤3 ссылок (больше никто не прочитает).',
    },
    {
      text: 'Decisions to be made — это **точки явного consensus**. Если в результате meeting вопрос остался открытым — meeting failed. Лучше формулировать decisions заранее, чем выяснять что не договорились через неделю.',
    },
  ],
  scoring: { baseScore: 8, firstAttemptBonus: 0, hintPenalty: 1, maxAttempts: 3 },
  achievementTriggers: [
    'author-junior',
    'sprint-07-bonus',
    'workspace-tools',
    'documentation',
    'curious-cat',
  ],
  estimateMin: 18,
  skills: {
    'Workspace Tools': 3,
    'Practice Lab': 5,
    Documentation: 4,
    'QA Concepts': 2,
  },
  requires: ['msw-api'],
};

// ────────────────────────────────────────────────────────────────────────────
// Sprint 12 · Performance + Security — B2-WEB-06..12
// Pattern mix: customModule (XSS/CSRF/perf) + AuthFlawBug (IDOR) +
// AccessibilityViolationBug (A11y · existing Block 1 kind) + Author rubrics.
// ────────────────────────────────────────────────────────────────────────────

// ─── B2-WEB-06 · S12 · XSS в comment field (customModule) ───────────────────

const B2_WEB_06: Scenario = {
  id: 'B2-WEB-06',
  sprint: 12,
  tier: 'required',
  mode: 'investigate',
  rank: 'security-watcher-junior',
  startRoute: '/artwork/lera-2026-014/comments',
  mountStrategy: 'iframe-route',
  customModule: 'block-2/xss-comment-field.tsx',
  title: 'XSS в comments · попробуй проинжектить script',
  brief:
    'Серж: «Comments на artwork pages — input от пользователя. Проверь sanitization. Попробуй safe XSS payload и посмотри, что произойдёт».',
  taskMd: [
    '**Контекст.** Атаки XSS (cross-site scripting) — top-3 OWASP risk. Если форма принимает текст и куда-то его выводит без proper sanitization, злоумышленник может внедрить `<script>` или `<img onerror>` и выполнить код в браузере жертв.',
    '',
    'Серж: «У нас в comments на artwork-странице input от пользователя. Тестируем sanitization. Попробуй safe XSS payload — увидим, фильтруется ли».',
    '',
    '**Что делать.** Открой `/artwork/lera-2026-014/comments`. В форму нового comment-а введи **safe XSS payload**:',
    '',
    '```',
    '<img src=x onerror="document.body.dataset.xssTriggered=\'true\'">',
    '```',
    '',
    'Это безопасный test payload — он не пытается украсть cookies или сделать что-то злонамеренное, только устанавливает атрибут на body, чтобы мы могли детектировать срабатывание.',
    '',
    '1. Введи payload в textarea, нажми «Отправить».',
    '2. Comment появится в списке. Открой DevTools (F12) → Console → введи: `document.body.dataset.xssTriggered`.',
    '3. Если возвращает `"true"` — XSS сработал, sanitization сломана.',
    '4. Если возвращает `undefined` или payload показывается в comment как escaped text (`&lt;img...`) — sanitization работает.',
    '5. Если sanitization сломана — кликни на сам comment в списке (это hotspot).',
    '',
    '**Что важно.** XSS появляется когда user input выводится в HTML без escaping. **Always-safe pattern:** браузер автоматически escape\'ит при использовании `textContent`, `.innerText`, или React JSX (`{userInput}`). **Опасный pattern:** `innerHTML`, `dangerouslySetInnerHTML`, `${userInput}` в template без sanitization. Когда review кода — ищи эти patterns. Когда тестируешь — пробуй XSS payloads на любые input fields, которые в итоге попадают в HTML.',
  ].join('\n'),
  hints: [
    {
      text: 'Введи payload в textarea. После отправки comment должен появиться. Если он показывается как «голый» текст (с угловыми скобками) — sanitization работает. Если в DOM появился `<img>` тег — sanitization сломана.',
    },
    {
      text: 'Откройте DevTools Console: `document.body.dataset.xssTriggered` → это canary. Если payload сработал, `onerror` выставит атрибут.',
    },
    {
      text: 'Если payload сработал — comment в списке отрисовался с реальным `<img>` тегом, и `dataset.xssTriggered === "true"`. Кликни на comment item в списке (там hotspot подсвечен).',
    },
  ],
  scoring: { baseScore: 9, firstAttemptBonus: 1, hintPenalty: 1, maxAttempts: 3 },
  achievementTriggers: [
    'detective-junior',
    'sprint-12-required',
    'security-watcher',
  ],
  estimateMin: 25,
  skills: {
    Security: 5,
    'Practice Lab': 5,
    'Test Design': 2,
    'Non-functional Testing': 2,
  },
  requires: ['msw-api', 'security-toolkit'],
};

// ─── B2-WEB-07 · S12 · CSRF на settings/profile (customModule) ──────────────

const B2_WEB_07: Scenario = {
  id: 'B2-WEB-07',
  sprint: 12,
  tier: 'required',
  mode: 'investigate',
  rank: 'security-watcher-junior',
  startRoute: '/settings/profile',
  mountStrategy: 'iframe-route',
  customModule: 'block-2/csrf-profile-update.tsx',
  title: 'CSRF · обновление профиля без CSRF token',
  brief:
    'Серж: «Форма настроек профиля обновляет email пользователя. Проверь — есть ли CSRF token? Если нет — атака возможна с другого сайта».',
  taskMd: [
    '**Контекст.** CSRF (Cross-Site Request Forgery) — атака, при которой злоумышленник заставляет жертву (через ссылку, image, скрытую форму на другом сайте) отправить request на сайт-мишень с её auth-cookie. Без защиты сайт принимает запрос как легитимный.',
    '',
    'Защита — CSRF token: уникальный токен в каждой форме, валидируется на server. Без token — request отклоняется.',
    '',
    'Серж: «Глянь форму настроек профиля. Она шлёт `PATCH /api/user/me` с новым email. Проверь, есть ли CSRF protection».',
    '',
    '**Что делать.** Открой `/settings/profile`. В форме изменения email:',
    '',
    '1. Открой DevTools → Network tab.',
    '2. Введи новый email (любой), нажми «Сохранить».',
    '3. В Network найди PATCH `/api/user/me` request.',
    '4. Глянь request payload (Request body). Headers (особенно `X-CSRF-Token` или подобный).',
    '5. Также глянь HTML формы (через Elements): есть ли скрытое поле `<input type="hidden" name="csrf_token" ...>`?',
    '6. Если ни в headers, ни в hidden field нет CSRF token — форма уязвима.',
    '7. Если уязвима — кликни на submit button формы (это hotspot).',
    '',
    '**Что важно.** CSRF token — стандартная защита от cross-site request forgery. Без него любая форма, изменяющая state, уязвима. Modern web API могут полагаться на SameSite cookies + custom headers — это альтернатива CSRF token. Но если форма «просто шлёт PATCH без token и без custom auth header» — это OWASP Top 10 finding. Always check forms that modify state (POST/PATCH/DELETE) на CSRF protection.',
  ].join('\n'),
  hints: [
    {
      text: 'CSRF token обычно или (а) в hidden input в форме, или (б) в HTTP header `X-CSRF-Token`. Глянь оба места — DevTools Elements + Network.',
    },
    {
      text: 'PATCH /api/user/me request: посмотри headers. Если видишь `X-CSRF-Token: <some hash>` — protection есть. Если только `Authorization: Bearer ...` или ничего custom — нет.',
    },
    {
      text: 'В этом сценарии CSRF token отсутствует и в форме, и в headers. Только cookie auth — недостаточно для CSRF protection. Кликни на «Сохранить» button (hotspot · `[data-form="profile-update"] button[type="submit"]`).',
    },
  ],
  scoring: { baseScore: 9, firstAttemptBonus: 1, hintPenalty: 1, maxAttempts: 3 },
  achievementTriggers: [
    'detective-junior',
    'sprint-12-required',
    'security-watcher',
  ],
  estimateMin: 22,
  skills: {
    Security: 5,
    'Practice Lab': 5,
    'Test Design': 2,
    'Non-functional Testing': 2,
  },
  requires: ['msw-api', 'auth-flow', 'security-toolkit'],
};

// ─── B2-WEB-08 · S12 · IDOR · order detail leak (auth-flaw) ─────────────────

const B2_WEB_08_BUG: AuthFlawBug = {
  id: 'B1',
  kind: 'auth-flaw',
  hotspot: '[data-api-response-field="orderId"]',
  flawType: 'idor',
  expectedBehavior:
    'GET /api/orders/{id} возвращает order только если current user = order owner OR имеет admin role. Иначе 403 Forbidden.',
  actualBehavior:
    'Endpoint возвращает любой order по ID без проверки ownership. Авторизованный пользователь может прочитать чужие orders, просто перебирая ID.',
  authorNote:
    'Classic IDOR (Insecure Direct Object Reference): server-side check «is this user authorized for this resource» отсутствует. Только authentication (есть session) проверяется, не authorization (does this session own this resource). Hotspot = orderId cell в response viewer (показывает чужой order ID + его данные · в реальном UI это возможно через URL guess или Burp request manipulation).',
};

const B2_WEB_08: Scenario = {
  id: 'B2-WEB-08',
  sprint: 12,
  tier: 'required',
  mode: 'investigate',
  rank: 'security-watcher-junior',
  startRoute: '/orders',
  mountStrategy: 'iframe-route',
  title: 'IDOR · чужие orders видны через URL',
  brief:
    'Серж: «Зайди в свои orders, попробуй открыть order по чужому ID (через URL bar). Доступ должен быть запрещён, а получится ли?»',
  taskMd: [
    '**Контекст.** IDOR (Insecure Direct Object Reference) — категория уязвимостей, где приложение проверяет «есть ли у тебя session», но не «можешь ли ты конкретно этот ресурс».',
    '',
    'Серж: «У тебя свои orders. У других юзеров — свои. Проверь, можешь ли ты прочитать чужие orders через manual URL guess».',
    '',
    '**Что делать.** Открой `/orders`. У тебя в списке твои 3 заказа: `ord_anya_001`, `ord_anya_002`, `ord_anya_003`.',
    '',
    '1. Кликни на первый order — попадёшь на `/orders/ord_anya_001` (твой order, всё ок).',
    '2. В URL bar измени `ord_anya_001` на `ord_lera_007` (заказ Леры) и нажми Enter.',
    '3. Что произошло?',
    '   - Если `403 Forbidden` или redirect к login — protection есть.',
    '   - Если показались детали чужого заказа — IDOR.',
    '4. Открой API Explorer (`🌐 API`). В response viewer ищи поле `orderId`.',
    '5. Если показался `ord_lera_007` (не твой) — это IDOR. Кликни на это поле в response viewer (это hotspot).',
    '',
    '**Что важно.** Authentication ≠ Authorization. Authentication: «who are you?» (есть session). Authorization: «can you do this?» (есть ли права на конкретный resource). IDOR появляется когда server проверяет только authentication. Защита — middleware/guard, который на каждый GET/PATCH/DELETE по `/api/orders/{id}` проверяет, что текущий user либо owner, либо admin. Тестировать просто: бери чужой ID из логов / другого аккаунта, пробуй access. Принцип: **никогда не полагайся на client-side filtering списка** (можно URL подменить).',
  ].join('\n'),
  bugs: [B2_WEB_08_BUG],
  hints: [
    {
      text: 'Меняй ID в URL: `/orders/ord_anya_001` → `/orders/ord_lera_007`. Если страница загружается с данными чужого order — IDOR. Если 403 — protection.',
    },
    {
      text: 'Глянь API Explorer (`🌐 API`). После navigation ты увидишь GET request на `/api/orders/ord_lera_007`. Какой статус? 200 OK с body? Или 403?',
    },
    {
      text: 'API вернул 200 OK + полный order Леры (orderId, items, total, address). Это IDOR — ты прочитал чужой order. Кликни на cell `orderId` в response viewer (показано `ord_lera_007`).',
    },
  ],
  scoring: { baseScore: 10, firstAttemptBonus: 1, hintPenalty: 1, maxAttempts: 3 },
  achievementTriggers: [
    'detective-junior',
    'sprint-12-required',
    'security-watcher',
    'api-master',
  ],
  estimateMin: 22,
  skills: {
    Security: 5,
    'Practice Lab': 5,
    API: 2,
    'Non-functional Testing': 2,
  },
  requires: ['msw-api', 'auth-flow', 'security-toolkit'],
};

// ─── B2-WEB-09 · S12 · Performance · N+1 fetches на cart load (customModule)

const B2_WEB_09: Scenario = {
  id: 'B2-WEB-09',
  sprint: 12,
  tier: 'required',
  mode: 'investigate',
  rank: 'detective-junior',
  startRoute: '/cart',
  mountStrategy: 'iframe-route',
  customModule: 'block-2/perf-cart-n-plus-one.tsx',
  title: 'Performance · cart грузит за 8 секунд',
  brief:
    'Аня: «Корзина с 8 товарами грузится 8 секунд!» Серж: «Открой DevTools waterfall, найди что неправильно».',
  taskMd: [
    '**Контекст.** Аня: «Я добавила 8 работ в корзину, открываю /cart — спиннер 8 секунд. С 1 товаром — мгновенно. С 2 — 2 секунды. С 8 — 8 секунд. Линейный рост. Что-то не так».',
    '',
    'Серж: «Похоже на N+1 fetches. Посмотри Network waterfall — там увидишь паттерн».',
    '',
    '**Что делать.** Открой `/cart` (с 8 товарами уже в корзине). Открой DevTools (F12) → Network tab → Filter: XHR/Fetch. Перезагрузи страницу.',
    '',
    '1. Посмотри список request-ов на загрузку cart.',
    '2. Сколько fetch-ов? К каким endpoint-ам?',
    '3. Если видишь паттерн «1 запрос за весь cart, потом 8 отдельных запросов за каждый artwork внутри» — это N+1.',
    '4. В waterfall view увидишь, что каждый artwork-fetch выполняется sequentially (один-за-другим, не parallel) — отсюда 8 секунд.',
    '5. Кликни на самый длинный single request в waterfall (это последний из 8 sequential fetches · hotspot).',
    '',
    '**Что важно.** N+1 fetch (или N+1 query) — классический performance bug. Frontend получает list IDs (1 fetch), потом для каждого ID делает отдельный fetch за деталями (N fetches). Время = O(N). **Правильное решение:** batch-endpoint, который принимает массив IDs и возвращает все данные одним fetch\'ом. Или backend join, отдающий expanded objects сразу.',
    '',
    'Тестировать просто: смотри Network waterfall на любой list-page. Если видишь N+1 паттерн — flag, измерь N (10 items? 100?), посчитай proper waterfall стоимость.',
  ].join('\n'),
  hints: [
    {
      text: 'Открой DevTools → Network → Filter Fetch/XHR → reload. Сколько requests видишь? К каким URL?',
    },
    {
      text: 'Если паттерн: GET /api/cart (1 request) + GET /api/artworks/<id> × 8 (по одному за каждый item в cart) — это N+1 fetch. Backend должен был отдать enriched cart в одном fetch.',
    },
    {
      text: 'В waterfall view 8 sequential GET /api/artworks/{id} requests, выполняются один за другим (зависимости через .then chain). Total = ~8s. Кликни на последний (8th) request в waterfall — это hotspot и worst-case latency.',
    },
  ],
  scoring: { baseScore: 9, firstAttemptBonus: 1, hintPenalty: 1, maxAttempts: 3 },
  achievementTriggers: [
    'detective-junior',
    'sprint-12-required',
    'perf-watcher',
  ],
  estimateMin: 22,
  skills: {
    'Non-functional Testing': 5,
    'Practice Lab': 5,
    'Test Design': 2,
    API: 1,
  },
  requires: ['msw-api', 'devtools-overlay'],
};

// ─── B2-WEB-10 · S12 · A11y · modal без focus trap ──────────────────────────

const B2_WEB_10_BUG = {
  id: 'B1',
  kind: 'accessibility-violation' as const,
  element: '[data-modal="checkout-confirm"]',
  wcagCriterion: '2.4.3 Focus Order (Level A) + 2.1.2 No Keyboard Trap (Level A)',
  axeRule: 'focus-trap',
  authorNote:
    'Modal "Confirm checkout" открывается, но focus не перехвачен внутри. Нажатие Tab уносит focus на элементы под backdrop\'ом (подложкой). Скрин-ридеры и keyboard юзеры не понимают, что modal — primary surface. WCAG 2.4.3 + 2.1.2 violation. Hotspot = modal container element.',
};

const B2_WEB_10: Scenario = {
  id: 'B2-WEB-10',
  sprint: 12,
  tier: 'required',
  mode: 'investigate',
  rank: 'detective-junior',
  startRoute: '/checkout',
  mountStrategy: 'iframe-route',
  title: 'A11y · modal без focus trap',
  brief:
    'Серж: «Чекаут открывает confirm-modal. Открой Tab — focus уезжает на форму под modal. WCAG нарушение. Найди элемент-hotspot».',
  taskMd: [
    '**Контекст.** Аксессибилити-аудит выявил: modal «Confirm checkout» (открывается после клика «Оформить заказ») не «trap\'ит» focus. Если пользователь жмёт Tab — focus уходит на элементы под подложкой (backdrop), пользователь теряется в DOM.',
    '',
    'Серж: «Это WCAG 2.4.3 + 2.1.2 violation. Воспроизведи через keyboard, посмотри куда улетает focus, кликни на modal element».',
    '',
    '**Что делать.** Открой `/checkout`. Заполни форму checkout (или используй pre-filled data). Нажми «Оформить заказ» → откроется modal «Confirm checkout».',
    '',
    '1. **Не используй мышь после открытия modal.** Только клавиатура.',
    '2. Жми Tab. Focus должен оставаться внутри modal (между «Confirm» и «Cancel» buttons).',
    '3. На самом деле: focus уезжает наружу — на форму checkout под backdrop\'ом.',
    '4. Это violation. Скрин-ридер не понимает, что modal — primary surface.',
    '5. Открой DevTools → Elements. Найди контейнер modal\'а (data-attribute `data-modal="checkout-confirm"`).',
    '6. Кликни на этот element в DevTools elements panel (или на сам modal в UI) — это hotspot.',
    '',
    '**Что важно.** Focus trap — стандартное поведение для modal. Без него keyboard users и screen reader users не понимают, что modal главный, продолжают «гулять» по странице, теряют контекст. Implementation простой: при открытии modal — установить focus на первый focusable element внутри. На Tab/Shift+Tab — циклить только внутри modal. На Esc — закрыть modal + return focus к trigger element. Если в твоём UI любой modal без focus trap — это automatic A11y fail audit.',
  ].join('\n'),
  bugs: [B2_WEB_10_BUG],
  hints: [
    {
      text: 'Не используй мышь — keyboard only. Открой modal через клик «Оформить заказ». Затем Tab. Куда улетает focus? Видно ли visual focus indicator (outline)?',
    },
    {
      text: 'Если focus уезжает на input fields формы checkout под backdrop\'ом — focus trap отсутствует. Должен был оставаться внутри modal.',
    },
    {
      text: 'Modal container имеет `data-modal="checkout-confirm"`. Найди его в DevTools Elements или просто кликни на сам modal в UI (selector `[data-modal="checkout-confirm"]`).',
    },
  ],
  scoring: { baseScore: 8, firstAttemptBonus: 1, hintPenalty: 1, maxAttempts: 3 },
  achievementTriggers: [
    'detective-junior',
    'sprint-12-required',
    'a11y-watcher',
  ],
  estimateMin: 18,
  skills: {
    'Non-functional Testing': 4,
    'Practice Lab': 5,
    'Test Design': 2,
  },
  requires: ['msw-api'],
};

// ─── B2-WEB-11 · S12 · Author · perf budget spec ────────────────────────────

const B2_WEB_11: Scenario = {
  id: 'B2-WEB-11',
  sprint: 12,
  tier: 'required',
  mode: 'author',
  rank: 'author-apprentice',
  startRoute: '/admin/confluence-shell/perf-budget',
  mountStrategy: 'iframe-route',
  title: 'Performance budget spec для checkout flow',
  brief:
    'Маша (PM): «Хотим формализовать perf-budget для checkout. Напиши spec — какие метрики, какие пороги, как измеряем».',
  taskMd: [
    '**Контекст.** Маша: «Не хочу чтобы release ушёл с медленным checkout. Нужен perf-budget — четкая метрика «зеленый/красный», которую CI меряет автоматически».',
    '',
    '**Что делать.** Напиши perf-budget spec для checkout flow. Spec должен содержать:',
    '',
    '**1. Scope:** какие endpoints/screens покрываем (хотя бы 3).',
    '',
    '**2. Метрики и budget per метрика:**',
    'Минимум 5 метрик из:',
    '- LCP (Largest Contentful Paint)',
    '- FCP (First Contentful Paint)',
    '- TTI (Time To Interactive)',
    '- CLS (Cumulative Layout Shift)',
    '- JS bundle size (KB gzip)',
    '- API response time p95',
    '- Total request count',
    '- Total bytes transferred',
    '',
    'Для каждой метрики — конкретное число (не «быстро», а `LCP < 2.5s`).',
    '',
    '**3. Measurement methodology:** как меряем (Lighthouse CI / Web Vitals / synthetic monitoring), на каком device/network, как часто.',
    '',
    '**4. Action thresholds:** что происходит если budget превышен (warn / block release / SLA breach notification).',
    '',
    '**5. Owner:** кто отвечает за спорные случаи и регулярный review.',
    '',
    '**Что важно.** Perf budget без чисел — пожелание, а не контракт. Хороший budget: (а) конкретные пороги (не «быстро»), (б) measurement methodology прописана (network simulation? device class?), (в) action thresholds — что делать когда нарушено. Без последнего budget игнорируется. Плохой budget: «должно быть быстро», «не должно тормозить» — ничего не значит.',
  ].join('\n'),
  rubric: {
    items: [
      {
        id: 'R1',
        text: 'Scope явно перечислен (≥3 endpoints/screens checkout flow)',
      },
      {
        id: 'R2',
        text: 'Минимум 5 метрик с конкретными порогами (не «быстро» · числа)',
      },
      {
        id: 'R3',
        text: 'LCP / FCP / TTI / CLS — хотя бы 2 из core web vitals',
      },
      {
        id: 'R4',
        text: 'Bundle size budget указан (KB gzip)',
      },
      {
        id: 'R5',
        text: 'API response time p95 указан (с числом ms)',
      },
      {
        id: 'R6',
        text: 'Measurement methodology явно: tool (Lighthouse / Web Vitals / synthetic), device/network class',
      },
      {
        id: 'R7',
        text: 'Action thresholds: что делает CI/team при breach (warn / block / notify)',
      },
      { id: 'R8', text: 'Owner указан (имя или role)' },
    ],
    passThreshold: 6,
  },
  sampleAnswer: [
    '**Title:** «Checkout flow performance budget · v1»',
    '',
    '**Scope:**',
    '- `/cart` (cart page · pre-checkout)',
    '- `/checkout` (checkout form · address + payment)',
    '- `/order/:id` (order confirmation)',
    '',
    '**Метрики и budget:**',
    '',
    '| Метрика | Budget | Action при breach |',
    '|---|---|---|',
    '| LCP (Largest Contentful Paint) | < 2.5s | warn → fix in next sprint |',
    '| FCP (First Contentful Paint) | < 1.8s | warn → fix in next sprint |',
    '| TTI (Time To Interactive) | < 3.0s | warn → fix in next sprint |',
    '| CLS (Cumulative Layout Shift) | < 0.1 | warn → fix in next sprint |',
    '| JS bundle size (route chunk gzip) | < 100 KB | block release · fix needed |',
    '| API response p95 | < 500ms | warn (1x), block (2x consecutive) |',
    '| Total request count (cart load) | < 12 | warn (если N+1 detected → block) |',
    '| Total bytes transferred (gzip) | < 250 KB | warn |',
    '',
    '**Measurement methodology:**',
    '- **Tool:** Lighthouse CI (на каждый PR)',
    '- **Device class:** Mobile · Moto G4 throttle · Slow 4G',
    '- **Frequency:** runs on every PR, blocks merge при breach JS bundle / N+1',
    '- **Real user monitoring:** Web Vitals collection через analytics для всех users (production sample)',
    '',
    '**Action thresholds:**',
    '- **Single-metric breach (один из metric):** warn в PR, fix в next sprint',
    '- **Bundle size breach OR N+1 detected:** block PR merge до фикса',
    '- **2x consecutive breach API p95:** SLA notification в #perf-alerts Slack',
    '',
    '**Owner:** Серж (QA lead) + Денис (lead dev) — review monthly · update budgets если platform shift',
  ].join('\n'),
  hints: [
    {
      text: 'Ключевая разница хорошего и плохого perf budget: **числа**. Не «быстро», а `LCP < 2.5s`. Не «небольшой bundle», а `< 100 KB gzip`. Без чисел budget = пожелание.',
    },
    {
      text: 'Action thresholds различай: (а) warn = fix в next sprint, (б) block release = блокирует merge, (в) SLA breach = notification + escalation. Не всё metric одинаково critical.',
    },
    {
      text: 'Methodology critical: одно и то же приложение на iPhone 15 Pro WiFi и Moto G4 4G покажет разные числа. Без device class budget non-actionable.',
    },
  ],
  scoring: { baseScore: 10, firstAttemptBonus: 0, hintPenalty: 1, maxAttempts: 3 },
  achievementTriggers: [
    'author-junior',
    'sprint-12-required',
    'perf-watcher',
    'documentation',
  ],
  estimateMin: 25,
  skills: {
    'Non-functional Testing': 5,
    'Practice Lab': 5,
    Documentation: 4,
    'Test Design': 2,
  },
  requires: ['msw-api'],
};

// ─── B2-WEB-12 · S12 · Bonus · Author · security review report ──────────────

const B2_WEB_12: Scenario = {
  id: 'B2-WEB-12',
  sprint: 12,
  tier: 'bonus',
  mode: 'author',
  rank: 'author-apprentice',
  startRoute: '/admin/confluence-shell/security-review',
  mountStrategy: 'iframe-route',
  title: 'Security review report для auth-flow release',
  brief:
    'Auth-flow обновили перед v1.0. Серж: «Напиши security review report — что проверял, что нашли, что осталось».',
  taskMd: [
    '**Контекст.** Серж проводил security audit auth-flow (login + signup + password-reset + token rotation). Нужно formalize findings и план действий в report для PM/leadership.',
    '',
    '**Что проверял Серж:**',
    '1. Сильность password validation (min length / complexity / common-password blocklist)',
    '2. Token storage (httpOnly · Secure · SameSite cookie attributes)',
    '3. Rate limiting на login attempts (защита от brute force)',
    '4. Password reset flow (token uniqueness · TTL · single-use)',
    '5. CSRF protection на state-changing endpoints',
    '',
    '**Что обнаружил:**',
    '- ✅ Password validation: passes (min 8 + не-only-letters · common-blocklist 1000 паролей)',
    '- ⚠️ Token storage: cookie без `Secure` flag (значит отправляется по HTTP) — fix needed',
    '- ✅ Rate limit: 5 attempts/15min, lockout 30min — correct',
    '- ⚠️ Password reset: token TTL = 24 hours (slishком долго · должно быть 15-30 мин) — fix needed',
    '- ❌ CSRF: missing token на PATCH /api/user/me — critical fix needed (B2-WEB-07 already filed)',
    '',
    '**Что делать.** Напиши security review report со структурой:',
    '',
    '1. **Title** + дата + scope',
    '2. **Summary** (1-2 предложения · что в release, какой security posture)',
    '3. **Findings table** (Severity · Category · Description · Recommendation · Status · Owner)',
    '4. **Critical action items** (что нужно ДО release)',
    '5. **Non-blocking improvements** (nice-to-have для следующих sprints)',
    '6. **Sign-off line** (кто approved release с данным security posture)',
    '',
    '**Что важно.** Security review report — это **формальный документ**, который leadership / compliance / PM читают и подписывают. Хороший report: (а) явно классифицирует severity (Critical / High / Medium / Low — не «плохо» · «не очень»), (б) каждый finding имеет actionable recommendation (не «улучшить» · конкретный шаг), (в) status каждого finding (open / in progress / fixed · с ETA). Без этих свойств report — это диагностика, не план.',
  ].join('\n'),
  rubric: {
    items: [
      { id: 'R1', text: 'Title + дата + scope явно указаны' },
      { id: 'R2', text: 'Summary 1-2 предложения' },
      {
        id: 'R3',
        text: 'Findings table: ≥4 findings с колонками Severity · Category · Description · Recommendation · Status',
      },
      {
        id: 'R4',
        text: 'Severity классифицирована (Critical / High / Medium / Low) для каждого finding',
      },
      {
        id: 'R5',
        text: 'Recommendation actionable (конкретный шаг · не «улучшить»)',
      },
      {
        id: 'R6',
        text: 'Critical action items секция выделена явно (что блокирует release)',
      },
      {
        id: 'R7',
        text: 'Non-blocking improvements секция (для следующего sprint)',
      },
      { id: 'R8', text: 'Sign-off line с именем approver' },
    ],
    passThreshold: 6,
  },
  sampleAnswer: [
    '**Title:** «Security Review · Auth Flow Release v1.0 · 2026-05-04»',
    '',
    '**Scope:** Login · Signup · Password Reset · Token Rotation · Session Management',
    '',
    '**Summary:**',
    'Auth flow готов к release v1.0 с **2 blocking findings** (cookie Secure flag · CSRF token) и **1 non-blocking improvement** (password reset token TTL). Critical CSRF фикс через 1 день закрывает release-blocker.',
    '',
    '**Findings:**',
    '',
    '| Severity | Category | Description | Recommendation | Status | Owner |',
    '|---|---|---|---|---|---|',
    '| Critical | CSRF | Missing CSRF token на PATCH /api/user/me | Add CSRF token middleware. См. BUG-451. | In progress · ETA 2026-05-05 | Денис |',
    '| High | Cookie | Token cookie без Secure flag (отправляется по HTTP) | Add Secure + SameSite=Strict | Open · ETA 2026-05-05 | Илья |',
    '| Medium | Reset flow | Password reset token TTL = 24h (слишком долго) | Сократить до 15-30 минут | Open · ETA next sprint | Денис |',
    '| Low | Logging | Login failures не логируются с IP (для analytics) | Add IP + user-agent в audit log | Backlog | TBD |',
    '',
    '**Critical action items (блокируют release):**',
    '1. CSRF token middleware (BUG-451) · Денис · ETA 2026-05-05',
    '2. Cookie Secure flag · Илья · ETA 2026-05-05',
    '',
    '**Non-blocking improvements (next sprint):**',
    '- Password reset TTL → 30 минут',
    '- Audit log enrichment (IP + UA)',
    '- Move к `__Host-` cookie prefix (extra hardening)',
    '',
    '**Sign-off:** Approval pending fix критических items. Approver: Серж (QA lead) · sign-off after BUG-451 + cookie fix verified.',
  ].join('\n'),
  hints: [
    {
      text: 'Severity классификация: **Critical** = release blocker (data loss · auth bypass · RCE). **High** = release possible но с план фикса. **Medium** = next sprint. **Low** = backlog. Не путай с priority (urgency для бизнеса).',
    },
    {
      text: 'Recommendation для каждого finding должен быть **actionable**: не «улучшить cookie security», а «add Secure + SameSite=Strict flags». Если recommendation == «improve», читатель не знает что делать.',
    },
    {
      text: 'Critical action items секция — это **release gate**. Если в ней пусто — можно подписывать release. Если есть items — release заблокирован до их закрытия. Это must-have для security report (без неё PM не знает, что блокирует).',
    },
  ],
  scoring: { baseScore: 10, firstAttemptBonus: 0, hintPenalty: 1, maxAttempts: 3 },
  achievementTriggers: [
    'author-junior',
    'sprint-12-bonus',
    'security-watcher',
    'documentation',
    'curious-cat',
  ],
  estimateMin: 28,
  skills: {
    Security: 5,
    'Practice Lab': 5,
    Documentation: 5,
    'Test Design': 2,
  },
  requires: ['msw-api', 'security-toolkit'],
};

// ────────────────────────────────────────────────────────────────────────────
// Block 2 catalog · ordered by sprint · within-sprint by domain
// ────────────────────────────────────────────────────────────────────────────

export const BLOCK_2_SCENARIOS: ReadonlyArray<Scenario> = [
  // Sprint 07 · Workspace tools (B2-WEB-01..05)
  B2_WEB_01,
  B2_WEB_02,
  B2_WEB_03,
  B2_WEB_04,
  B2_WEB_05,
  // Sprint 08 · SQL basic (B2-SQL-01..05)
  B2_SQL_01,
  B2_SQL_02,
  B2_SQL_03,
  B2_SQL_04,
  B2_SQL_05,
  // Sprint 09 · HTTP & REST API basics (B2-API-01..06)
  B2_API_01,
  B2_API_02,
  B2_API_03,
  B2_API_04,
  B2_API_05,
  B2_API_06,
  // Sprint 10 · Postman MASTER (B2-API-07..12)
  B2_API_07,
  B2_API_08,
  B2_API_09,
  B2_API_10,
  B2_API_11,
  B2_API_12,
  // Sprint 11 · SQL deep (B2-SQL-06..10)
  B2_SQL_06,
  B2_SQL_07,
  B2_SQL_08,
  B2_SQL_09,
  B2_SQL_10,
  // Sprint 12 · Perf+Security (B2-WEB-06..12)
  B2_WEB_06,
  B2_WEB_07,
  B2_WEB_08,
  B2_WEB_09,
  B2_WEB_10,
  B2_WEB_11,
  B2_WEB_12,
];
